/**
 * src/components/spatial/SpatialObjectsMapLayer.tsx
 *
 * Top-level component for the GeoLink spatial-objects subsystem.
 *
 * Responsibilities:
 *   1. Wrap the host's existing map instance with a provider.
 *   2. Add the optional MapTiler raster overlay UNDER all GeoTrust
 *      layers when the user enables it (Mapbox stays the basemap).
 *   3. Create a single GeoJSON source backed by GeoLink's render
 *      endpoint envelope `{ provider, featureCollection, style }`.
 *   4. Mount the four extrusion sublayers (building / floor / zone /
 *      slice) which read from that shared source.
 *   5. Wire click + hover handlers across all four layers.
 *   6. Render the controls cluster + interaction panel.
 *
 * App.tsx keeps full control of the underlying Mapbox instance — it
 * just renders <SpatialObjectsMapLayer map={…} /> inside its existing
 * map container. The MapTiler overlay is purely additive, so all
 * existing Mapbox features (3D buildings, agents, geofences, …) keep
 * working unchanged.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  createSpatialMapProvider,
  type SpatialMapProvider,
} from '../../services/spatial/mapProvider';
import {
  geoLinkSpatialObjectsClient,
} from '../../services/spatial/geoLinkSpatialObjectsClient';
import { mapApiClient } from '../../services/spatial/map/mapApiClient';
import type {
  SpatialObjectFeature,
  SpatialObjectFeatureCollection,
  SpatialRenderProvider,
} from '../../services/spatial/geoLinkTypes';
import {
  SpatialObjectsProvider,
  useSpatialObjects,
  type MapTilerOverlayState,
  type SpatialObjectsView,
  type SpatialObjectsFilters,
} from './SpatialObjectsContext';
import {
  SPATIAL_LAYER_IDS,
  SPATIAL_MAPTILER_RASTER_LAYER_ID,
  SPATIAL_MAPTILER_RASTER_SOURCE_ID,
  SPATIAL_SOURCE_ID,
} from './spatialLayerUtils';
import SpatialObjectExtrusionLayer from './SpatialObjectExtrusionLayer';
import SpatialObjectFloorLayer from './SpatialObjectFloorLayer';
import SpatialObjectZoneLayer from './SpatialObjectZoneLayer';
import SpatialObjectSliceLayer from './SpatialObjectSliceLayer';
import SpatialObjectPointLayer from './SpatialObjectPointLayer';
import SpatialObjectsControls from './SpatialObjectsControls';
import SpatialNearbyPanel from './SpatialNearbyPanel';
import {
  deriveEventPresentation,
  flagBandSlabs,
  flagBandSlabsWithin,
  resolveFlagPaint,
  type FlagPaint,
} from './spatialFeatureDerivation';
import {
  asMultiPolygonGeometry,
  extractPolygonParts,
  geometryOfParts,
  parsePartsArray as parseTiersArray,
  partsFitGeometry as tierPairIsConsistent,
  resolveCacheWrite,
  resolveExtrusion,
  type PolygonPart,
} from './polygonExtrusion';
import { flagIntentFor, learnFlagIntent } from './spatialFlagIntentCache';
import SpatialInspector from './SpatialInspectorPanels';
import Tiles3DLoaderPanel from './Tiles3DLoaderPanel';
import SpatialObjectInteractionPanel, {
  type InteractionBridges,
} from './SpatialObjectInteractionPanel';
import { MapDataProvider, useMapDataOptional } from './MapDataContext';
import SpatialDeckOverlay from './SpatialDeckOverlay';
import MapAggregateLayersController from './MapAggregateLayersController';
import MapEntityPopover from './MapEntityPopover';
import SpatialObjectTooltip from './SpatialObjectTooltip';
import SpatialObjectChatCard from './SpatialObjectChatCard';
import SpatialObjectDepositCard from './SpatialObjectDepositCard';
import SpatialObjectListenCard from './SpatialObjectListenCard';
import SpatialEventCard from './SpatialEventCard';
import SpatialEventComposer from './SpatialEventComposer';
import { isNarrowViewport } from '../map/mapSurfaces';
import { createMapActions } from '../../services/spatial/mapActions';
import { toTooltipTreasury, useObjectTreasury } from './useObjectTreasury';
import { useLiveBroadcasts } from '../../hooks/useLiveBroadcasts';
import { useEventCounts } from '../../hooks/useEventCounts';
import { featureCenter, pickProximityGreeting } from './proximityGreeting';
import { haversineMeters } from '../../services/spatial/spatialProximity';
import { deriveSpatialTooltipModel } from './spatialTooltipModel';
import { useMapHover } from '../map/useMapHover';
import {
  getMapTilerKey,
  getSpatialRenderProvider,
  isSpatialDeckRendererEnabled,
  isSpatialDeckInterleaved,
  isMapApiEnabled,
  isMapLayersEnabled,
  type MapProviderId,
} from '../../services/spatial/spatialEnv';
import { geoLinkSpatialObjectsAdminClient } from '../../services/spatial/geoLinkSpatialObjectsAdminClient';
import {
  bandColourForSlab,
  getCachedFlagBands,
  warmFlagBands,
} from '../../utils/countryFlagPalette';
import {
  crownSplit,
  refitAxis,
  stripeAxisFor,
  stripeSlices,
  type StripeAxis,
} from '../../utils/flagStripeGeometry';

export interface SpatialObjectsMapLayerProps {
  /**
   * The host's existing map instance (mapbox-gl / maplibre-gl /
   * @maptiler/sdk). REQUIRED — we never construct one ourselves.
   */
  map: unknown;
  /**
   * Optional basemap-provider override. When omitted we read
   * `VITE_MAP_PROVIDER` (or `REACT_APP_MAP_PROVIDER`) from env, which
   * defaults to "mapbox" so the existing app keeps working untouched.
   */
  providerOverride?: MapProviderId;
  /**
   * Override which `?provider=` is sent to GeoLink's /render endpoint.
   * Defaults to `getSpatialRenderProvider()` from env.
   */
  renderProviderOverride?: SpatialRenderProvider;
  /** Caller's wallet public key (when known). Forwarded to /interact. */
  publicKey?: string | null;
  /** Caller's last-known location. Forwarded to /interact. */
  location?: { latitude: number; longitude: number; accuracy?: number } | null;
  /** Bridges to the host app's wallet / passkey / payment / location flows. */
  bridges?: InteractionBridges;
  /**
   * Optional preconditions per action verb. Lets the host enforce
   * "wallet required for execute", "payment required for buy" etc.
   */
  preconditionsFor?: React.ComponentProps<typeof SpatialObjectInteractionPanel>['preconditionsFor'];
  /** Whether to render the floating control cluster. Default: true. */
  showControls?: boolean;
  /**
   * Whether to render the bottom-left spatial Inspector (layer panel /
   * attribute table / style editor). Default: true. The inspector
   * collapses to a small button until opened, so it's unobtrusive.
   */
  showInspector?: boolean;
  /**
   * Whether to render the OGC 3D Tiles loader panel (deck.gl
   * MapboxOverlay + Tile3DLayer), letting an operator stream a
   * `tileset.json` (photogrammetry / BIM / point cloud) onto the map.
   * Default: false — it's an advanced/admin affordance.
   */
  showTiles3D?: boolean;
  /**
   * Optional host-provided cinematic camera move. When supplied, the
   * inspector's attribute-row click uses this (the app's shared
   * `flyToWithMode` 3D zoom-in: pitch 60, adds 3D buildings on arrival)
   * instead of an abrupt jump. Receives a validated [lng, lat]. If
   * omitted, the inspector falls back to a safe `jumpTo`.
   */
  cinematicFlyTo?: (center: [number, number]) => void;
  /** Anchor for the controls card. */
  controlsStyle?: React.CSSProperties;
  /**
   * Polling interval for /spatial-objects/render in ms when the camera
   * isn't moving. Defaults to 30s. Camera move events trigger an
   * additional refresh debounced to 600ms.
   */
  refreshIntervalMs?: number;
  /** Initial overrides for the control state. */
  initialView?: Partial<SpatialObjectsView>;
  initialFilters?: Partial<SpatialObjectsFilters>;
  initialMapTilerOverlay?: Partial<MapTilerOverlayState>;
  /** Called whenever the data refreshes — useful for "X buildings nearby" badges. */
  onDataLoaded?: (fc: SpatialObjectFeatureCollection) => void;
}

/** Build the MapTiler XYZ tile URL template for a given style + key. */
function buildMapTilerTileUrl(style: string, key: string): string {
  // MapTiler raster XYZ endpoint, per https://docs.maptiler.com/cloud/api/maps/.
  return `https://api.maptiler.com/maps/${encodeURIComponent(style)}/{z}/{x}/{y}.png?key=${encodeURIComponent(key)}`;
}

/**
 * Stable key for de-duplicating accumulated features. Prefers the full
 * feature id (which keeps per-tier setback features distinct via their
 * "#tN" suffix), then any canonical object-id property, then a geometry
 * hash as a last resort.
 */
function featureKeyOf(f: SpatialObjectFeature): string {
  if (f.id != null) return String(f.id);
  const p = (f.properties ?? {}) as Record<string, unknown>;
  for (const k of ['objectId', 'object_id', 'spatial_object_id', 'spatialObjectId', 'id']) {
    const v = p[k];
    if (typeof v === 'string' && v) return v;
    if (typeof v === 'number') return String(v);
  }
  try { return JSON.stringify(f.geometry ?? {}); } catch { return Math.random().toString(36); }
}

/** Drop parent + per-tier entries so a fat MultiPolygon can't linger next to #tN setbacks. */
function removeObjectFromAcc(acc: Map<string, SpatialObjectFeature>, objectId: string): void {
  const id = String(objectId || '').trim();
  if (!id) return;
  for (const key of Array.from(acc.keys())) {
    if (key === id || key.startsWith(`${id}#`)) acc.delete(key);
  }
}

function upsertFeaturesIntoAcc(
  acc: Map<string, SpatialObjectFeature>,
  features: SpatialObjectFeature[],
): void {
  const ids = new Set<string>();
  for (const f of features) {
    const id = objectIdOf(f);
    if (id) ids.add(id);
  }
  Array.from(ids).forEach((id) => removeObjectFromAcc(acc, id));
  for (const f of features) acc.set(featureKeyOf(f), f);
}

/**
 * GeoLink's `/api/map/spatial-objects` GeoJSON endpoint rejects viewports
 * whose bbox area exceeds ~100 deg² (`bbox_too_large`). We stay just under
 * that so a valid request never round-trips into a 400.
 */
const MAP_API_MAX_BBOX_DEG2 = 95;

/** Approximate bbox area in deg², antimeridian-aware. */
function mapBboxAreaDeg2(
  bounds: { sw: { lng: number; lat: number }; ne: { lng: number; lat: number } },
): number {
  const latSpan = Math.abs(bounds.ne.lat - bounds.sw.lat);
  let lngSpan = bounds.ne.lng - bounds.sw.lng;
  if (lngSpan < 0) lngSpan += 360; // viewport crosses the antimeridian
  return Math.abs(lngSpan) * latSpan;
}

/** Zoom at/above which building extrusion is meaningful — gates eager detail. */
const EAGER_DETAIL_MIN_ZOOM = 14;
/** Max /spatial-objects/:id detail fetches kicked off per refresh (cost cap). */
const EAGER_DETAIL_MAX_PER_REFRESH = 24;

/** True when a summary feature already carries a usable extrusion height. */
function featureHasHeight(f: SpatialObjectFeature): boolean {
  const p = (f?.properties ?? {}) as Record<string, unknown>;
  const h = Number(p.heightMeters ?? p.height);
  return Number.isFinite(h) && h > 0;
}

/**
 * Canonical object id for a feature (map-API `id`/`objectId` or feature id).
 * Ignores per-tier "#tN" suffixes so detail enrichment keys on the parent.
 */
function objectIdOf(f: SpatialObjectFeature): string | null {
  if (f.id != null) return String(f.id).split('#')[0];
  const p = (f.properties ?? {}) as Record<string, unknown>;
  for (const k of ['objectId', 'object_id', 'spatialObjectId', 'id']) {
    const v = p[k];
    if (typeof v === 'string' && v) return v;
    if (typeof v === 'number') return String(v);
  }
  return null;
}

/**
 * Per-polygon extrusion metadata. Stored as `metadata.tiers` for historical
 * reasons; see `polygonExtrusion` for why it never meant a stack.
 */
type SpatialTier = PolygonPart;

type FlagIntent = FlagPaint;

/**
 * What this object is wearing: the wire, then memory, then the palette.
 * The rules are in `resolveFlagPaint`; this only supplies the lookups.
 */
function resolveFlagIntent(
  meta: Record<string, unknown>,
  countryCode: string | undefined,
  objectId: string | undefined,
): FlagIntent | null {
  return resolveFlagPaint(
    meta,
    countryCode,
    flagIntentFor(objectId),
    getCachedFlagBands,
  );
}

/**
 * Countries whose flag some feature in this batch wants to wear.
 *
 * Includes remembered intents, so the palette is warmed for a building
 * whose summary feature says nothing about a flag it is known to wear.
 * Without that the cache and the palette would each wait for the other.
 */
function flagCountriesIn(features: SpatialObjectFeature[]): string[] {
  const out = new Set<string>();
  for (const f of features) {
    const props = (f?.properties ?? {}) as Record<string, unknown>;
    const meta = (props.metadata ?? {}) as Record<string, unknown>;
    const raw = (meta.surface_flag ?? meta.surfaceFlag) as { enabled?: unknown } | undefined;

    if (!raw || typeof raw !== 'object' || raw.enabled !== true) {
      const remembered = flagIntentFor(objectIdOf(f))?.countryCode;
      if (remembered) out.add(remembered);
      continue;
    }
    for (const c of [
      (raw as Record<string, unknown>).countryCode,
      (raw as Record<string, unknown>).country_code,
      props.countryCode,
      props.country_code,
      meta.countryCode,
      meta.country_code,
    ]) {
      if (typeof c !== 'string') continue;
      const s = c.trim().toUpperCase();
      if (/^[A-Z]{2}$/.test(s)) { out.add(s); break; }
    }
  }
  return Array.from(out);
}


/**
 * A building's parts as one feature per vertical stripe, under a crown.
 *
 * An extruded slab is one solid colour, so a stripe running *down* a tower
 * has to be a narrower tower: each part is cut into strips along the
 * building's shared direction, across its own width, and each strip
 * extruded in its own colour. Strips are grouped by stripe rather than by
 * part, so a building of six parts still yields one feature per colour
 * instead of six.
 *
 * The crown is the one horizontal cut a vertical arrangement makes — the
 * hoist colour capping the tower, with the columns hanging below it — and
 * it keeps the full footprint, uncut.
 *
 * Empty when nothing survived the cut, which is the caller's cue to fall
 * back to horizontal bands rather than leave the building bare.
 */
function columnFeatures(
  source: SpatialObjectFeature,
  parts: number[][][][],
  axis: StripeAxis,
  flag: FlagPaint,
  split: ReturnType<typeof crownSplit>,
  props: SpatialObjectFeature['properties'],
  keyFor: (suffix: string) => string | undefined,
): SpatialObjectFeature[] {
  if (flag.layout.kind !== 'columns') return [];
  const { columns, crown } = flag.layout;

  type Piece = {
    suffix: string;
    geometry: GeoJSON.Geometry;
    colour: string;
    low: number;
    high: number;
  };
  const pieces: Piece[] = [];

  if (split.body) {
    const byStripe = new Map<number, number[][][][]>();
    for (const poly of parts) {
      // Across this part's own width, so a narrow shaft on a wide podium
      // wears the whole flag rather than one stripe of it.
      const fitted = refitAxis(axis, poly);
      if (!fitted) continue;
      for (const slice of stripeSlices(poly, fitted, columns.length)) {
        const held = byStripe.get(slice.index) ?? [];
        held.push(slice.polygon);
        byStripe.set(slice.index, held);
      }
    }
    for (const index of Array.from(byStripe.keys()).sort((a, b) => a - b)) {
      pieces.push({
        suffix: `c${index}`,
        geometry: geometryOfParts(byStripe.get(index)!),
        colour: columns[index],
        low: split.body.low,
        high: split.body.high,
      });
    }
  }

  if (split.crown && crown) {
    pieces.push({
      suffix: 'crown',
      geometry: geometryOfParts(parts),
      colour: crown,
      low: split.crown.low,
      high: split.crown.high,
    });
  }

  return pieces.map((piece) => ({
    ...source,
    id: keyFor(piece.suffix),
    geometry: piece.geometry as SpatialObjectFeature['geometry'],
    properties: {
      ...props,
      colorKey: piece.colour,
      fill_color: piece.colour,
      flagCountryCode: flag.countryCode,
      baseHeightMeters: piece.low,
      heightMeters: piece.high,
      base_height: piece.low,
      height: piece.high,
      // Marks these as already expanded, so the eager-detail pass does not
      // keep asking the server for setbacks it has no use for here.
      ...(props.totalTiers == null ? { totalTiers: pieces.length } : {}),
    },
  }));
}

/**
 * Payload from Manage Spatial Objects after create/update. The live map
 * layer caches tiers here because GeoLink's /render and /api/map summary
 * often omit `metadata.tiers`, which collapses stepped towers back into
 * one fat MultiPolygon extrusion when the import preview is cleared.
 */
export type SpatialObjectUpsertDetail = {
  id: string;
  geometry: GeoJSON.Geometry;
  heightMeters?: number;
  baseHeightMeters?: number;
  tiers?: SpatialTier[];
  name?: string;
  objectType?: string;
  colorKey?: string;
  /**
   * True only for a real user save (Create / Update button) whose
   * geometry+tiers pair came straight from the import. Authoritative
   * pairs may replace a richer cached pair; opportunistic re-seeds
   * (admin list rows, wire echoes) may not.
   */
  authoritative?: boolean;
};

/** Module-level cache so tiers survive refresh even if the API strips them. */
const tierCacheByObjectId = new Map<string, SpatialTier[]>();
const geometryCacheByObjectId = new Map<string, GeoJSON.Geometry>();

/**
 * What the server's footprint looked like when each cached pair was last
 * confirmed, so an intentional geometry edit can be told apart from
 * GeoLink restating the same building in a lossier form. Absent means the
 * pair came from a local save the server has not echoed yet.
 */
const wireSignatureByObjectId = new Map<string, string | null>();

/**
 * How many people are at each event, by object id.
 *
 * Module-level for the same reason the two caches above are: the
 * normalizer runs per feature with no access to component state, and
 * this is data that arrives out of band.
 *
 * Deliberately not persisted and deliberately never written back to
 * the object. A count is a reading taken now; the tier cache survives
 * a refresh because a building's shape does not change, and an
 * attendance does. Stale is worse than absent here, so nothing
 * restores this on load.
 */
const eventCountByObjectId = new Map<string, number>();

/**
 * Feed the presence layer's counts in.
 *
 * Replaces rather than merges: an event missing from a fresh answer
 * has no count, and keeping the last one would paint an empty square
 * as busy for as long as the tab stayed open.
 */
export function setEventCounts(counts: Record<string, number> | null | undefined): void {
  eventCountByObjectId.clear();
  if (!counts) return;
  for (const [id, count] of Object.entries(counts)) {
    if (Number.isInteger(count) && count >= 0) eventCountByObjectId.set(id, count);
  }
}

/** Survives hard refresh — /render often drops tiers + rewrites MultiPolygon. */
const TIER_LS_KEY = 'geotrust:spatial-tier-v1';
const TIER_LS_MAX = 100;

type PersistedTierEntry = {
  tiers: SpatialTier[];
  geometry?: GeoJSON.Geometry;
  /** @see wireSignatureByObjectId */
  wireSignature?: string | null;
  updatedAt: number;
};

function readPersistedTierStore(): Record<string, PersistedTierEntry> {
  try {
    const raw = localStorage.getItem(TIER_LS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object') return {};
    return parsed as Record<string, PersistedTierEntry>;
  } catch {
    return {};
  }
}

function writePersistedTierFor(
  id: string,
  tiers: SpatialTier[],
  geometry?: GeoJSON.Geometry,
  wireSignature?: string | null,
): void {
  try {
    const store = readPersistedTierStore();
    store[id] = {
      tiers,
      geometry: geometry ?? store[id]?.geometry,
      wireSignature: wireSignature !== undefined ? wireSignature : store[id]?.wireSignature,
      updatedAt: Date.now(),
    };
    const ranked = Object.entries(store).sort(
      (a, b) => (b[1].updatedAt || 0) - (a[1].updatedAt || 0),
    );
    const trimmed: Record<string, PersistedTierEntry> = {};
    for (let i = 0; i < Math.min(ranked.length, TIER_LS_MAX); i++) {
      trimmed[ranked[i][0]] = ranked[i][1];
    }
    localStorage.setItem(TIER_LS_KEY, JSON.stringify(trimmed));
  } catch { /* private mode / quota */ }
}

function removePersistedTier(id: string): void {
  try {
    const store = readPersistedTierStore();
    if (!(id in store)) return;
    delete store[id];
    localStorage.setItem(TIER_LS_KEY, JSON.stringify(store));
  } catch { /* ignore */ }
}

/**
 * Hydrate in-memory caches from localStorage (hard-refresh recovery).
 * Only self-consistent (geometry, tiers) pairs are loaded — earlier builds
 * could persist a server-mangled pair (tiers referencing more polygons than
 * the geometry has), which can never render and would block wire updates.
 */
function hydrateTierCachesFromStorage(): void {
  const store = readPersistedTierStore();
  for (const id of Object.keys(store)) {
    const entry = store[id];
    const tiers = parseTiersArray(entry?.tiers);
    if (entry?.geometry && tierPairIsConsistent(tiers, entry.geometry)) {
      tierCacheByObjectId.set(id, tiers);
      geometryCacheByObjectId.set(id, entry.geometry);
      wireSignatureByObjectId.set(id, entry.wireSignature ?? null);
    } else {
      removePersistedTier(id);
    }
  }
}
hydrateTierCachesFromStorage();

function cacheUpsertDetail(detail: SpatialObjectUpsertDetail): void {
  const id = String(detail.id || '').trim();
  if (!id) return;
  const tiers = parseTiersArray(detail.tiers);
  let geom: GeoJSON.Geometry | undefined = detail.geometry;
  if (geom) geom = asMultiPolygonGeometry(geom) ?? geom;

  const write = resolveCacheWrite({
    parts: tiers,
    geometry: geom,
    authoritative: !!detail.authoritative,
    existingPartCount: tierCacheByObjectId.get(id)?.length ?? 0,
  });

  switch (write.action) {
    case 'store':
      tierCacheByObjectId.set(id, tiers);
      geometryCacheByObjectId.set(id, geom!);
      // A user save describes a building the server has not echoed yet, so
      // there is no server footprint to hold this pair to. The next
      // normalize adopts whatever the server returns, and an edit after
      // that is then detectable.
      if (write.resetSignature) wireSignatureByObjectId.set(id, null);
      writePersistedTierFor(id, tiers, geom, write.resetSignature ? null : undefined);
      return;

    case 'forget':
      clearCachedObject(id);
      if (write.rememberGeometry) geometryCacheByObjectId.set(id, geom!);
      return;

    case 'remember-geometry-only':
      // Never clobber an import MultiPolygon that cached tiers index into.
      if (!geometryCacheByObjectId.has(id)) geometryCacheByObjectId.set(id, geom!);
      return;

    case 'skip':
      return;
  }
}

function clearCachedObject(objectId: string): void {
  const id = String(objectId || '').trim();
  if (!id) return;
  tierCacheByObjectId.delete(id);
  geometryCacheByObjectId.delete(id);
  wireSignatureByObjectId.delete(id);
  localAuthorityUntilMs.delete(id);
  removePersistedTier(id);
}

/**
 * True when these features already carry per-polygon extents.
 *
 * Counting features was the old test, which made a legitimately
 * single-part object look unexpanded forever — so enrichment kept asking
 * for a detail it had already applied. The `#t` suffix the expansion
 * stamps on each id is the reliable marker, and it is there whether the
 * object has one part or forty-five.
 */
function featuresAreTierExpanded(features: SpatialObjectFeature[]): boolean {
  if (features.length === 0) return false;
  return features.some((f) => {
    const p = (f.properties ?? {}) as Record<string, unknown>;
    return Number(p.totalTiers) >= 1 || String(f.id ?? '').includes('#t');
  });
}

/**
 * After Import → Create we inject correct tiers locally. Soft /render
 * refresh + enrich must not clobber that for a short window (GeoLink
 * often returns GeometryCollection / stripped tiers right after create).
 */
const localAuthorityUntilMs = new Map<string, number>();

function markLocalAuthority(objectId: string, ttlMs = 180_000): void {
  const id = String(objectId || '').trim();
  if (!id) return;
  localAuthorityUntilMs.set(id, Date.now() + ttlMs);
}

function isLocalAuthority(objectId: string): boolean {
  const id = String(objectId || '').trim();
  if (!id) return false;
  const until = localAuthorityUntilMs.get(id);
  if (until == null) return false;
  if (Date.now() > until) {
    localAuthorityUntilMs.delete(id);
    return false;
  }
  return true;
}

/**
 * Normalise a raw GeoLink spatial feature (from the render envelope OR the
 * normalized map API) into one-or-more render-ready features:
 *   - hoist `metadata.color_key` → top-level `colorKey`/`fill_color`
 *   - coalesce a canonical `objectId`
 *   - set the short `linkHas*` flags used by the layer filter expressions
 *   - expand `metadata.tiers` (stepped towers like Burj Khalifa) into one
 *     Feature per architectural setback so the fill-extrusion / deck layer
 *     paints each (base,height) band instead of a single fat block.
 *
 * Shared by the bulk viewport fetch AND detail enrichment so a building
 * imported with tiers renders identically no matter which path loaded it.
 */
function normalizeSpatialFeatures(f: SpatialObjectFeature): SpatialObjectFeature[] {
  const props = f.properties;
  const meta = { ...((props.metadata ?? {}) as Record<string, unknown>) };
  const metaColor =
    (typeof meta.color_key === 'string' && meta.color_key) ||
    (typeof meta.colorKey === 'string' && meta.colorKey) ||
    null;
  const colorKey =
    (typeof props.colorKey === 'string' && props.colorKey) ||
    (typeof props.fill_color === 'string' && props.fill_color) ||
    metaColor ||
    undefined;
  const objectId =
    (props.objectId != null ? String(props.objectId) : null) ??
    ((props as Record<string, unknown>).id != null
      ? String((props as Record<string, unknown>).id)
      : null) ??
    (f.id != null ? String(f.id).split('#')[0] : null);

  // Every payload passes through here, so this is where a flag seen once
  // becomes a flag remembered — and where one that has been turned off is
  // forgotten.
  learnFlagIntent(objectId, meta);

  // Coalesce an extrusion height + base from the many field names GeoLink's
  // detail / render / map-API payloads use. The deck layer + native
  // fill-extrusion both read top-level `heightMeters`/`baseHeightMeters`
  // (see spatialSemantics.getSpatialElevation); the map-API `/spatial-
  // objects/:id` detail nests it differently than the legacy `/render`
  // envelope, which is why enriched buildings loaded their footprint but
  // extruded flat. Normalising here lets both render paths agree.
  const pAny = props as Record<string, unknown>;
  const pickPositive = (...vals: unknown[]): number | undefined => {
    for (const v of vals) {
      const n = Number(v);
      if (Number.isFinite(n) && n > 0) return n;
    }
    return undefined;
  };
  const pickNonNegative = (...vals: unknown[]): number | undefined => {
    for (const v of vals) {
      const n = Number(v);
      if (Number.isFinite(n) && n >= 0) return n;
    }
    return undefined;
  };
  const heightM = pickPositive(
    pAny.heightMeters, pAny.height, pAny.height_meters, pAny.heightM,
    meta.heightMeters, meta.height, (meta as Record<string, unknown>).height_meters,
    (meta as Record<string, unknown>).building_height, (meta as Record<string, unknown>).buildingHeight,
  );
  const baseM = pickNonNegative(
    pAny.baseHeightMeters, pAny.base_height, pAny.baseHeight, pAny.base_height_meters,
    meta.baseHeightMeters, meta.base_height, (meta as Record<string, unknown>).min_height,
  );

  // ── Choose an ATOMIC (geometry, tiers) pair ─────────────────────────
  // The ordering and the reasons live in `resolveExtrusion`. What matters
  // here is the consequence: a remembered pair is only ever consulted when
  // the server's own payload cannot be indexed into, and only while the
  // server still agrees about where the building is. Before that check
  // existed, clearing the extents off an edited building and saving it
  // brought the old shape straight back on the next refresh.
  let wireTiers = parseTiersArray(meta.tiers);
  if (wireTiers.length === 0) wireTiers = parseTiersArray(pAny.tiers);

  const cachedTiers = objectId ? tierCacheByObjectId.get(objectId) ?? null : null;
  const cachedGeom  = objectId ? geometryCacheByObjectId.get(objectId) ?? null : null;

  const plan = resolveExtrusion({
    wireGeometry: f.geometry,
    wireParts: wireTiers,
    cached: cachedTiers && cachedGeom
      ? {
          geometry: cachedGeom,
          parts: cachedTiers,
          wireSignature: objectId ? wireSignatureByObjectId.get(objectId) ?? null : null,
        }
      : null,
  });

  const geometry = plan.geometry as typeof f.geometry;
  const rawTiers = plan.parts;
  if (plan.parts.length > 0) meta.tiers = plan.parts;

  if (objectId) {
    if (plan.staleCache) {
      clearCachedObject(objectId);
    } else if (plan.source === 'wire') {
      // Self-consistent server pair — remember it so later summary
      // responses (which omit tiers entirely) can still expand this object.
      cacheUpsertDetail({
        id: objectId,
        geometry: f.geometry as GeoJSON.Geometry,
        tiers: wireTiers,
      });
    } else if (plan.adoptWireSignature != null) {
      // First sight of the server's version of a locally saved pair. Pin
      // the pair to it, so a later edit is an edit and not a re-encode.
      wireSignatureByObjectId.set(objectId, plan.adoptWireSignature);
      writePersistedTierFor(objectId, cachedTiers!, cachedGeom!, plan.adoptWireSignature);
    }
  }

  // Hoist Map API summary fields so the interaction panel can show a
  // country flag + display name even before /interactions returns.
  const pickIso2 = (...vals: unknown[]): string | undefined => {
    for (const v of vals) {
      if (typeof v !== 'string') continue;
      const s = v.trim().toUpperCase();
      if (/^[A-Z]{2}$/.test(s)) return s;
    }
    return undefined;
  };
  const countryCode = pickIso2(
    pAny.countryCode, pAny.country_code,
    meta.countryCode, meta.country_code, meta.country,
  );
  const displayName =
    (typeof pAny.displayName === 'string' && pAny.displayName.trim()) ||
    (typeof pAny.name === 'string' && pAny.name.trim()) ||
    (typeof meta.name === 'string' && meta.name.trim()) ||
    undefined;

  // The whole derived set, not just the state. One producer for the
  // registry in `derivedPresentation` means a new event-derived
  // property arrives stamped everywhere rather than in whichever
  // caller remembered to compute it.
  const eventPresentation = deriveEventPresentation(
    meta,
    Date.now(),
    objectId ? eventCountByObjectId.get(objectId) : undefined,
  );

  const baseProps = {
    ...props,
    metadata: meta,
    ...(objectId ? { objectId } : {}),
    ...(colorKey ? { colorKey, fill_color: colorKey } : {}),
    ...(heightM != null ? { heightMeters: heightM, height: heightM } : {}),
    ...(baseM != null ? { baseHeightMeters: baseM, base_height: baseM } : {}),
    ...(countryCode ? { countryCode } : {}),
    ...(displayName ? { displayName, name: (pAny.name as string | undefined) ?? displayName } : {}),
    linkHasExecutionRule: props.executionRuleId != null,
    linkHasContract:      props.contractAddress != null,
    linkHasEvent:         meta.event != null,
    linkHasRealEstate:    meta.realEstate != null || meta.real_estate != null,
    // Transient by construction: computed from stored metadata and the
    // current time, stamped onto the GeoJSON the map renders, and
    // stripped again by the write path. Stored geometry is untouched.
    ...eventPresentation,
  };

  const flag = resolveFlagIntent(meta, countryCode, objectId ?? undefined);

  // Expand stepped tiers for MultiPolygon AND GeometryCollection-of-
  // Polygons. Skipping GeometryCollection used to paint every Atlantis
  // part from 0→peak and destroy the floating mid-tower bands.
  const allPolys = extractPolygonParts(geometry);

  // Which way a vertical arrangement's stripes run is decided by the whole
  // building, so every part of a tower is cut the same way; how wide they
  // are is decided per part, inside columnFeatures.
  const columnAxis = flag?.layout.kind === 'columns' && allPolys
    ? stripeAxisFor(allPolys)
    : null;
  // One part is enough to take this branch. A polygon whose extent runs
  // from 55m to 85m is not described by the object's own base and height,
  // and the length of the list is not what makes the metadata meaningful.
  if (rawTiers.length > 0 && allPolys && allPolys.length > 0) {
    // Best effort only: tiers indexing past the geometry are dropped. This
    // branch is reached with out-of-range indices only when NO consistent
    // (geometry, tiers) pair exists anywhere — the consistent-pair caching
    // above (and cacheUpsertDetail's validation) keeps the cache clean, so
    // don't write anything back from here.
    const tiers = rawTiers.filter((t) => t.polygon_index < allPolys.length);
    if (tiers.length > 0) {
      // Max over the heights that exist. Seeding with tier 0 meant one
      // part with no height left the peak unusable for every other part,
      // which flattened a flag onto a single band.
      const peak = tiers.reduce(
        (m, x) => (Number.isFinite(x.height) && x.height > m ? x.height : m),
        0,
      );
      const key = (suffix: string) => (objectId != null
        ? `${objectId}#${suffix}`
        : (f.id != null ? `${String(f.id)}#${suffix}` : undefined));

      return tiers.flatMap((t, i) => {
        const geom = {
          type: 'Polygon',
          coordinates: allPolys[t.polygon_index],
        } as typeof f.geometry;

        const tierProps = {
          ...baseProps,
          totalTiers: tiers.length,
          // Preserve peak for tooltips without using the clicked setback.
          peakHeightMeters: peak,
        };

        // Columns first: this tier's own polygon cut against the whole
        // building's stripe axis, under whatever crown reaches it.
        if (flag && columnAxis && flag.layout.kind === 'columns') {
          const columned = columnFeatures(
            f,
            [allPolys[t.polygon_index]],
            columnAxis,
            flag,
            crownSplit(t.min_height, t.height, peak, flag.layout.crownShare),
            { ...tierProps, tierIndex: i },
            (suffix) => key(`t${i}${suffix}`),
          );
          // Empty only for a footprint too small to cut, which falls
          // through to horizontal bands rather than going unpainted.
          if (columned.length > 0) return columned;
        }

        // A tier tall enough to cross a band boundary is cut at it, so the
        // flag belongs to the tower rather than to whichever parts OSM
        // happened to record. Without this a single 0→peak shaft wore one
        // stripe and the rest of the flag never appeared.
        const cuts = flag
          ? flagBandSlabsWithin(t.min_height, t.height, peak, flag.bands.length)
          : [];

        if (cuts.length > 1) {
          return cuts.map((slab) => ({
            ...f,
            id: key(`t${i}b${slab.bandIndex}`),
            geometry: geom,
            properties: {
              ...tierProps,
              colorKey:   flag!.bands[slab.bandIndex],
              fill_color: flag!.bands[slab.bandIndex],
              flagCountryCode: flag!.countryCode,
              baseHeightMeters: slab.low,
              heightMeters:     slab.high,
              base_height:      slab.low,
              height:           slab.high,
              tierIndex: i,
            },
          }));
        }

        // One band, or none: the tier keeps its own extent.
        const band = flag
          ? (cuts.length === 1
            ? flag.bands[cuts[0].bandIndex]
            : bandColourForSlab(flag.bands, t.min_height, t.height, peak))
          : null;

        return [{
          ...f,
          id: key(`t${i}`),
          geometry: geom,
          properties: {
            ...tierProps,
            ...(band ? { colorKey: band, fill_color: band, flagCountryCode: flag!.countryCode } : {}),
            baseHeightMeters: t.min_height,
            heightMeters:     t.height,
            base_height:      t.min_height,
            height:           t.height,
            tierIndex: i,
          },
        }];
      });
    }
  }

  // A plain box wears a flag by being sliced into one slab per band —
  // the same per-slab features the tier expansion above already renders.
  if (flag && heightM != null) {
    if (columnAxis && allPolys && flag.layout.kind === 'columns') {
      const columned = columnFeatures(
        f,
        allPolys,
        columnAxis,
        flag,
        crownSplit(baseM ?? 0, heightM, heightM, flag.layout.crownShare),
        { ...baseProps, peakHeightMeters: heightM },
        (suffix) => (objectId != null
          ? `${objectId}#${suffix}`
          : (f.id != null ? `${String(f.id)}#${suffix}` : undefined)),
      );
      if (columned.length > 0) return columned;
    }

    const slabs = flagBandSlabs(baseM ?? 0, heightM, flag.bands.length);
    if (slabs.length > 0) {
      const slabGeometry = geometry ?? f.geometry;
      return slabs.map((slab) => ({
        ...f,
        id: objectId != null
          ? `${objectId}#b${slab.bandIndex}`
          : (f.id != null ? `${String(f.id)}#b${slab.bandIndex}` : undefined),
        geometry: slabGeometry,
        properties: {
          ...baseProps,
          colorKey:   flag.bands[slab.bandIndex],
          fill_color: flag.bands[slab.bandIndex],
          flagCountryCode: flag.countryCode,
          baseHeightMeters: slab.low,
          heightMeters:     slab.high,
          base_height:      slab.low,
          height:           slab.high,
          tierIndex: slab.tierIndex,
          totalTiers: slabs.length,
          peakHeightMeters: heightM,
        },
      }));
    }
  }

  return [{ ...f, geometry: geometry ?? f.geometry, properties: baseProps }];
}

/**
 * Inner component — runs inside the SpatialObjectsProvider so it can
 * read the shared context.
 */
const SpatialObjectsMapLayerInner: React.FC<SpatialObjectsMapLayerProps> = ({
  map,
  providerOverride,
  renderProviderOverride,
  publicKey,
  location,
  bridges,
  preconditionsFor,
  showControls = true,
  showInspector = true,
  showTiles3D = false,
  cinematicFlyTo,
  controlsStyle,
  refreshIntervalMs = 30_000,
  onDataLoaded,
}) => {
  const { view, mapTiler, renderPipeline } = useSpatialObjects();
  // Phase 2 (Map API 3D) still *fetches* via the normalized /api/map and
  // pushes features into MapDataContext for aggregate deck layers, but
  // buildings always paint through native Mapbox fill-extrusion (deck.gl
  // has no per-feature base height, which deformed tiered towers).
  const mapData = useMapDataOptional();
  const deckEnabled = useMemo(
    () => renderPipeline === 'phase2' && isSpatialDeckRendererEnabled(),
    [renderPipeline],
  );
  const deckInterleaved = useMemo(() => isSpatialDeckInterleaved(), []);
  // When on the Phase 2 pipeline, spatial objects are read from GeoLink's
  // normalized /api/map/spatial-objects instead of the legacy /render
  // envelope. The summary contract omits height/tier detail, so this is
  // deliberately kept off for the default (legacy) pipeline.
  const mapApiEnabled = useMemo(
    () => renderPipeline === 'phase2' && isMapApiEnabled(),
    [renderPipeline],
  );
  // The Phase 2 aggregate layers (country policy, rules, presence, agents,
  // drones) draw through the deck overlay from /api/map/* and are INDEPENDENT
  // of the spatial-objects pipeline — they're on in both legacy and phase2.
  // Only meaningful when a shared MapDataContext is present.
  const aggregateEnabled = useMemo(() => isMapLayersEnabled() && !!mapData, [mapData]);
  // The deck overlay must mount whenever it owns spatial objects (phase2) OR
  // is needed purely for the aggregate layers.
  const deckOverlayMounted = deckEnabled || aggregateEnabled;
  const [provider, setProvider] = useState<SpatialMapProvider | null>(null);

  /**
   * The one route from a feature to the camera.
   *
   * The host's cinematic move is handed over as policy rather than
   * called directly, which is what keeps camera arithmetic out of the
   * click handlers that only wanted to say "show me this object".
   */
  // Which event surface is open, and which object is marked. Both are
  // the map's own state, which is why `mapActions` asks for them
  // rather than reaching for them: a feature that could set either
  // would be deciding what is on screen.
  const [eventTarget, setEventTarget] = useState<string | null>(null);
  const [highlightedObjectId, setHighlightedObjectId] = useState<string | null>(null);
  // Creating an event at a venue, or editing one. Carries the host
  // because creation happens at the building, not in the abstract.
  const [eventEditTarget, setEventEditTarget] = useState<
  { hostObjectId: string; eventObjectId?: string | null } | null
  >(null);

  const mapActions = useMemo(
    () => (provider
      ? createMapActions({
        provider,
        cinematic: cinematicFlyTo ?? null,
        openEvent: (objectId) => setEventTarget(objectId),
        highlight: (objectId) => setHighlightedObjectId(objectId),
      })
      : null),
    [provider, cinematicFlyTo],
  );

  const [selectedFeature, setSelectedFeature] = useState<SpatialObjectFeature | null>(null);
  // Snapshot of the most recently loaded features, fed to the Inspector's
  // attribute table. Kept separate from the map source so the panel can
  // list/filter without re-querying the renderer.
  const [loadedFeatures, setLoadedFeatures] = useState<SpatialObjectFeature[]>([]);
  // Accumulated superset of every feature we've fetched for the life of
  // this provider, keyed by a stable per-feature id. The /render endpoint
  // is bbox-clamped, so a naive "replace on every refresh" collapses both
  // the map source AND the attribute table down to whatever is in the
  // current viewport — which is why flying to one building used to wipe
  // every other object out of view and out of the Attributes tab. By
  // merging into this map and feeding the union to the source + inspector
  // we keep previously-seen objects visible as the camera moves. Capped
  // so it can't grow without bound. Reset when the provider changes.
  const accumulatedRef = useRef<Map<string, SpatialObjectFeature>>(new Map());
  const [anchorPx, setAnchorPx] = useState<{ x: number; y: number } | undefined>(undefined);
  // Transient hover, kept strictly apart from the click-selected panel
  // above: hovering a building shows a card built from properties we
  // already hold, and only a click is allowed to load anything.
  const hover = useMapHover<SpatialObjectFeature>(
    useCallback((f: SpatialObjectFeature) => {
      const props = f.properties as Record<string, unknown>;
      const id = props.objectId != null ? String(props.objectId) : null;
      return id || (f.id != null ? String(f.id).split('#')[0] : null);
    }, []),
  );
  const hoverActions = hover.actions;
  // An open conversation. Deliberately separate from both the hover
  // card and the selected feature: a visitor can be talking to one
  // building while inspecting another, and neither should close the
  // other.
  const [chatTarget, setChatTarget] = useState<{
    objectId: string;
    name: string;
    accent: string;
    point: { x: number; y: number };
    /**
     * Where the object is, as opposed to where the visitor is.
     *
     * The card's shortcuts ask what can be done nearby, and a visitor
     * who has not shared a location still deserves an answer about the
     * building in front of them. Carrying the object's own centre means
     * that answer is available without pretending to know where the
     * visitor is standing.
     */
    center?: { lng: number; lat: number } | null;
  } | null>(null);
  // An open deposit. Separate from the conversation for the same
  // reason: they are different intentions and closing one is not
  // closing the other.
  const [depositTarget, setDepositTarget] = useState<{
    objectId: string;
    name: string;
    accent: string;
    point: { x: number; y: number };
  } | null>(null);
  // An open player, for the same reason again.
  const [listenTarget, setListenTarget] = useState<{
    objectId: string;
    name: string;
    accent: string;
    point: { x: number; y: number };
    distanceMeters: number | null;
  } | null>(null);

  /**
   * Make room for a card that is about to open.
   *
   * On a wide viewport all three may coexist — they now know about
   * each other through the overlay coordinator and place themselves
   * clear. On a phone there is no clear position to find: two of these
   * cards side by side leaves neither readable, so the one being
   * opened is the one that survives.
   *
   * Measured at the moment of opening rather than tracked, because
   * this is a decision about right now and a card already on screen
   * should not be dismissed by rotating the device.
   */
  const closeCompetingCards = useCallback(
    (keep: 'chat' | 'listen' | 'deposit' | 'event' | 'inspector') => {
      if (!isNarrowViewport(window.innerWidth)) return;
      if (keep !== 'chat') setChatTarget(null);
      if (keep !== 'listen') setListenTarget(null);
      if (keep !== 'deposit') setDepositTarget(null);
      // The event surface joins the same rule rather than getting an
      // exception: two panels on a phone leaves neither usable, and
      // `mapSurfaces` already counts this one as major.
      if (keep !== 'event') { setEventTarget(null); setEventEditTarget(null); }
      // So does the details panel. It is the surface a tap opens, and
      // on a phone it was the one competing with every card here while
      // being exempt from the rule they all follow.
      if (keep !== 'inspector') { setSelectedFeature(null); setAnchorPx(undefined); }
    },
    [],
  );

  /**
   * Open a conversation with an object.
   *
   * Three callers now — the hover card, the details panel and the
   * proximity greeting — and they must agree on more than they used
   * to: which cards close, and that the card is told where the object
   * is so its shortcuts can answer without a device location. One
   * function is cheaper than three that drift.
   */
  const openChat = useCallback(
    (input: {
      objectId: string;
      name: string;
      accent: string;
      point: { x: number; y: number };
      center?: { lng: number; lat: number } | null;
    }) => {
      closeCompetingCards('chat');
      setChatTarget({
        objectId: input.objectId,
        name: input.name,
        accent: input.accent,
        point: input.point,
        center: input.center ?? null,
      });
    },
    [closeCompetingCards],
  );

  // What is speaking anywhere, in one poll for the whole map. This is
  // what lets the hover card be honest about audio without paying a
  // request per building hovered.
  const liveAudio = useLiveBroadcasts({ enabled: view.enabled });
  // Level 2. Only a pin — a click, or opening the deposit card — asks
  // the chain anything; see useObjectTreasury for why that boundary is
  // drawn here and not inside the tooltip.
  const treasury = useObjectTreasury(
    depositTarget?.objectId
      ?? (hover.pinned?.payload?.properties as Record<string, unknown> | undefined)?.objectId as string | undefined
      ?? null,
  );
  // Derived, never fetched: the card is a projection of properties the
  // renderer already had, plus whatever a previous pin happened to load.
  const hoverTooltip = useMemo(() => {
    if (!hover.hovered) return null;
    const props = hover.hovered.payload.properties as Record<string, unknown> | undefined;
    const id = props?.objectId != null ? String(props.objectId) : null;
    const model = deriveSpatialTooltipModel(hover.hovered.payload, {
      treasury: toTooltipTreasury(treasury.cachedFor(id)),
      // Only once the poll has answered. Before that the card says
      // "unknown" and still offers Listen, rather than telling every
      // building it is silent because we have not looked yet.
      liveAudio: liveAudio.ready ? !!liveAudio.get(id) : null,
    });
    return model
      ? { model, point: hover.hovered.point, feature: hover.hovered.payload }
      : null;
  }, [hover.hovered, treasury, liveAudio]);
  // styleEpoch increments every time the underlying basemap style
  // reloads (`style.load`) or when our self-heal detects a missing
  // source/layer. We pass this into the layer components' effect-deps
  // so they tear down + re-add their layers — Mapbox wipes ALL custom
  // sources and layers on `setStyle` / style reloads, and React
  // wouldn't know unless we explicitly signal it.
  const [styleEpoch, setStyleEpoch] = useState(0);
  // sourceReady gates the child layer components on the GeoJSON source
  // having been added to the underlying map. Without this, React's
  // child-before-parent effect ordering would run each layer's
  // `addLayer` BEFORE the parent's `addSource`, and Mapbox throws
  // "source 'gl-spatial-objects' not found" inside its event handler
  // — which gets silently swallowed by the emitter, leaving the layer
  // missing and the map blank.
  const [sourceReady, setSourceReady] = useState(false);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cameraDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Latest-ref for `refresh`. The timer / camera / event effects invoke it
  // through this ref so they DON'T list `refresh` in their deps — `refresh`
  // changes identity on every MapData state update (it closes over the
  // context value), and re-running those effects on each change would tear
  // down + re-arm the interval and re-fire a fetch on every setStatus,
  // producing an infinite request loop (ERR_INSUFFICIENT_RESOURCES).
  const refreshRef = useRef<(() => Promise<void>) | null>(null);
  // Points at `enrichObjectsDetail` (defined below) so `refresh` can kick
  // off eager building-detail fetches without depending on it directly.
  const enrichRef = useRef<((ids: string[]) => void) | null>(null);
  // Throttle the self-heal styleEpoch bump so a transient mid-load
  // diagnostic can't pin the spatial subsystem in an infinite remount
  // loop (every refresh tick → bump epoch → cleanup tears down source
  // → next refresh sees source MISSING → bump epoch again …).
  const lastSelfHealRef = useRef(0);

  const renderProvider = renderProviderOverride ?? getSpatialRenderProvider();

  // Listen for basemap style reloads and bump the styleEpoch so every
  // dependent effect (source + layers) re-runs and re-adds itself on
  // top of the fresh style. This is the canonical way to keep custom
  // overlays alive across `setStyle` calls.
  //
  // Also arm `idle` — Mapbox diff-style updates often skip `style.load`
  // and only emit `styledata`, which left Map API 3D extrusions missing
  // until a later refresh (~1 min).
  useEffect(() => {
    if (!provider) return;
    const native = provider.nativeMap as {
      on?: (e: string, h: () => void) => void;
      off?: (e: string, h: () => void) => void;
      once?: (e: string, h: () => void) => void;
      getSource?: (id: string) => unknown;
    };
    if (typeof native.on !== 'function' || typeof native.off !== 'function') return;
    const bump = () => setStyleEpoch(e => e + 1);
    const onStyleLoad = () => {
      bump();
      try { native.once?.('idle', bump); } catch { /* ignore */ }
    };
    // Diff path: if our spatial source vanished, force a remount cycle.
    const onStyleData = () => {
      try {
        if (!native.getSource?.(SPATIAL_SOURCE_ID) && accumulatedRef.current.size > 0) {
          bump();
        }
      } catch { /* ignore */ }
    };
    native.on('style.load', onStyleLoad);
    native.on('styledata', onStyleData);
    return () => {
      try { native.off?.('style.load', onStyleLoad); } catch { /* ignore */ }
      try { native.off?.('styledata', onStyleData); } catch { /* ignore */ }
    };
  }, [provider]);

  // ─── Attach / detach the provider ──────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    let createdProvider: SpatialMapProvider | null = null;
    if (!map) return;

    // Fresh provider → fresh accumulator (don't leak features across map
    // instances / basemap-provider swaps).
    accumulatedRef.current = new Map();

    createSpatialMapProvider(map, providerOverride)
      .then(p => {
        if (cancelled) {
          p.dispose();
          return;
        }
        createdProvider = p;
        setProvider(p);
      })
      .catch(err => {
        // We deliberately don't crash the host app if the provider
        // can't be created — the spatial subsystem just stays dark.
      });

    return () => {
      cancelled = true;
      if (createdProvider) {
        createdProvider.dispose();
      }
      setProvider(null);
    };
  }, [map, providerOverride]);

  // ─── MapTiler raster overlay (the complement layer) ───────────────
  // Adds a single raster source + raster layer underneath every other
  // GeoTrust layer so the Mapbox basemap labels and our spatial-object
  // extrusions still paint on top. We re-add when the style changes
  // (different tile URL) and remove cleanly when toggled off.
  useEffect(() => {
    if (!provider) return;
    const key = getMapTilerKey();
    if (!mapTiler.enabled || !key) {
      // Tear down any existing overlay.
      if (provider.hasLayer(SPATIAL_MAPTILER_RASTER_LAYER_ID)) {
        provider.removeLayer(SPATIAL_MAPTILER_RASTER_LAYER_ID);
      }
      if (provider.hasSource(SPATIAL_MAPTILER_RASTER_SOURCE_ID)) {
        provider.removeSource(SPATIAL_MAPTILER_RASTER_SOURCE_ID);
      }
      return;
    }

    let cancelled = false;
    provider.whenReady(() => {
      if (cancelled) return;
      // Always rebuild from scratch when the style changes — the tile
      // URL template is baked into the source spec.
      if (provider.hasLayer(SPATIAL_MAPTILER_RASTER_LAYER_ID)) {
        provider.removeLayer(SPATIAL_MAPTILER_RASTER_LAYER_ID);
      }
      if (provider.hasSource(SPATIAL_MAPTILER_RASTER_SOURCE_ID)) {
        provider.removeSource(SPATIAL_MAPTILER_RASTER_SOURCE_ID);
      }
      provider.addRasterSource(SPATIAL_MAPTILER_RASTER_SOURCE_ID, {
        type:        'raster',
        tiles:       [buildMapTilerTileUrl(mapTiler.style, key)],
        tileSize:    256,
        attribution: '© MapTiler © OpenStreetMap contributors',
        minzoom:     0,
        maxzoom:     22,
      });

      // Insert the raster layer ABOVE the basemap fill but BELOW the
      // first symbol (label) layer so country/place labels stay readable.
      // We delegate `beforeId` resolution to the provider implementation
      // (which already finds the first text symbol layer for us when
      // beforeId is omitted).
      provider.addLayer({
        id:     SPATIAL_MAPTILER_RASTER_LAYER_ID,
        type:   'raster',
        source: SPATIAL_MAPTILER_RASTER_SOURCE_ID,
        paint:  {
          'raster-opacity': mapTiler.opacity,
        },
      });
    });

    return () => {
      cancelled = true;
      // Don't tear down on every dep change — only when the user
      // disables the overlay (handled at the top of this effect).
    };
    // We rebuild when style / opacity change so the raster source
    // points at the right URL template and the paint opacity reflects
    // the slider.
  }, [provider, mapTiler.enabled, mapTiler.style, mapTiler.opacity]);

  // Cheap path: when only the opacity changed, just patch the paint
  // property instead of rebuilding (the previous effect handles that
  // too, but this keeps the paint up-to-date even for transient drag
  // states on the slider).
  useEffect(() => {
    if (!provider) return;
    if (!provider.hasLayer(SPATIAL_MAPTILER_RASTER_LAYER_ID)) return;
    provider.setPaintProperty(SPATIAL_MAPTILER_RASTER_LAYER_ID, 'raster-opacity', mapTiler.opacity);
  }, [provider, mapTiler.opacity]);

  // ─── Spatial-objects GeoJSON source ────────────────────────────────
  // Runs ONCE per provider, NOT per styleEpoch. Earlier versions
  // re-ran this effect on every styleEpoch bump and tore the source
  // down in the cleanup, which created an infinite teardown loop:
  //   refresh → diagnostic logs source MISSING → bumps styleEpoch →
  //   cleanup tears down source → next refresh logs MISSING again …
  // The `whenReady` callback never had a chance to fire before the
  // next teardown wiped its registration.
  //
  // Instead we keep ONE long-lived effect that:
  //   1. Adds the source on first mount (waiting for style if needed).
  //   2. Listens for `style.load` and re-adds after Mapbox wipes
  //      everything on a basemap swap.
  //   3. Only removes the source when the provider itself goes away.
  useEffect(() => {
    if (!provider) return;
    let cancelled = false;

    // Pull the underlying mapbox-gl Map (or maplibre-gl) instance.
    // For the diagnostic + the direct-add fallback below we need full
    // access to addSource/getSource/on/off.
    const native = provider.nativeMap as unknown as {
      addSource?: (id: string, src: unknown) => void;
      getSource?: (id: string) => unknown;
      removeSource?: (id: string) => void;
      isStyleLoaded?: () => boolean;
      on?: (e: string, h: (ev: unknown) => void) => void;
      off?: (e: string, h: (ev: unknown) => void) => void;
      once?: (e: string, h: (ev: unknown) => void) => void;
    };

    // Mapbox emits silent failures via `map.on('error', ...)` instead
    // of throwing. The previous polling loop had no visibility into
    // these events, so a "source already registered" / "invalid source
    // spec" rejection looked exactly like a queued-but-pending add.
    // Capture them so the next failure is diagnosable.
    const onMapError = (ev: unknown) => {
      const e = (ev as { error?: { message?: string } })?.error;
      const msg = e?.message ?? String(ev);
      // Match anything that mentions our spatial source/layer ids OR
      // the canonical `gl-spatial-*` prefix our layer components use.
      // The validator surfaces errors with paths like
      // `layers.gl-spatial-building-extrusion.layout: ...`, which we
      // want to capture here so they are NOT silent.
      if (typeof msg === 'string' && msg.toLowerCase().includes('gl-spatial')) {
      }
    };
    try { native.on?.('error', onMapError); } catch { /* ignore */ }

    let pollTimer: ReturnType<typeof setTimeout> | null = null;
    let pollAttempts = 0;
    let verifiedOnce = false;
    const POLL_INTERVAL_MS = 100;
    const POLL_MAX_ATTEMPTS = 100; // 10s total

    const tryAddAndVerify = (logTag: string): boolean => {
      if (cancelled) return true; // cancelled — stop trying
      // Don't even try until the basemap style is loaded — calling
      // addSource against a half-loaded style is the exact case Mapbox
      // silently no-ops (no throw, no error event, just doesn't take).
      if (!provider.isStyleLoaded()) return false;

      // Probe the native map directly. The provider abstraction's
      // `hasSource` swallows exceptions and the `addSource` impl drops
      // a `tolerance: undefined` field into the source spec — both
      // can mask the real reason Mapbox is rejecting our add. Going
      // through `nativeMap.*` here gives us symmetric, observable
      // behaviour identical to what the preview map does (which works).
      const probe = (): boolean => {
        try { return !!native.getSource?.(SPATIAL_SOURCE_ID); } catch { return false; }
      };

      if (!probe()) {
        try {
          // Plain spec — NO `tolerance: undefined` and NO `generateId`
          // (the latter is unnecessary; we already key features on
          // their stable spatial-object UUID via the `objectId`
          // property the layer filters / popups consume).
          native.addSource?.(SPATIAL_SOURCE_ID, {
            type: 'geojson',
            data: { type: 'FeatureCollection', features: [] },
          });
        } catch (e) {
          return false;
        }
      }
      const ok = probe();
      if (ok) {
        verifiedOnce = true;
        setSourceReady(true);
      }
      return ok;
    };

    // Polling loop. We need this because:
    //   - `isStyleLoaded()` lies — returns true mid-style-mutation.
    //   - `addSource` then silently no-ops (no throw, no error event).
    //   - The `idle` event NEVER fires while teleport / cinematic
    //     animations are running, which is most of the user's session.
    //   - `styledata` only fires on style mutations — flyTo/easeTo
    //     don't trigger it.
    // So event-driven retry is unreliable. A 100ms poll for up to 10s
    // is the only thing that actually catches the moment Mapbox's
    // style queue commits. Stops the moment the source is verified.
    const startPoll = () => {
      if (cancelled || pollTimer) return;
      const tick = () => {
        pollTimer = null;
        if (cancelled) return;
        if (tryAddAndVerify('poll')) return;
        pollAttempts += 1;
        if (pollAttempts >= POLL_MAX_ATTEMPTS) {
          return;
        }
        pollTimer = setTimeout(tick, POLL_INTERVAL_MS);
      };
      pollTimer = setTimeout(tick, POLL_INTERVAL_MS);
    };

    const ensureSource = () => {
      if (cancelled) return;
      pollAttempts = 0;
      // Try immediately — covers the common case where the style is
      // already loaded by the time SpatialObjectsMapLayer mounts.
      if (tryAddAndVerify('immediate')) return;
      // Otherwise wait for whenReady (style.load / styledata-with-
      // styleLoaded=true) AND start a backup poll. Whichever path
      // gets there first wins; the other becomes a no-op once
      // sourceReady=true.
      provider.whenReady(() => {
        if (cancelled) return;
        if (tryAddAndVerify('whenReady')) return;
        startPoll();
      });
      // Belt-and-suspenders: if the style takes > 100ms to load, the
      // poll catches it without us depending on whenReady firing
      // promptly. (whenReady's `styledata` listener can miss the load
      // moment in some basemap swap orderings.)
      startPoll();
    };

    // Initial attach.
    ensureSource();

    // Re-attach after every basemap style reload — Mapbox wipes ALL
    // custom sources/layers on `setStyle`, so we have to re-add ours.
    const onStyleLoad = () => {
      setSourceReady(false);
      // Clear any in-flight poll from the previous style and start
      // fresh — pollAttempts resets inside ensureSource().
      if (pollTimer) {
        clearTimeout(pollTimer);
        pollTimer = null;
      }
      ensureSource();
    };
    // Diff-style swaps may skip style.load; recover if our source is gone
    // after we had previously attached it (ignore the initial load flurry).
    const onStyleData = () => {
      if (cancelled || !verifiedOnce) return;
      try {
        if (!native.getSource?.(SPATIAL_SOURCE_ID)) {
          verifiedOnce = false;
          setSourceReady(false);
          ensureSource();
        }
      } catch { /* ignore */ }
    };
    if (typeof native?.on === 'function') {
      native.on('style.load', onStyleLoad);
      native.on('styledata', onStyleData);
    }

    return () => {
      cancelled = true;
      if (pollTimer) {
        clearTimeout(pollTimer);
        pollTimer = null;
      }
      try { native?.off?.('style.load', onStyleLoad); } catch { /* ignore */ }
      try { native?.off?.('styledata', onStyleData); } catch { /* ignore */ }
      try { native?.off?.('error', onMapError); } catch { /* ignore */ }
      setSourceReady(false);
      try {
        if (native?.getSource?.(SPATIAL_SOURCE_ID)) {
          native.removeSource?.(SPATIAL_SOURCE_ID);
        }
      } catch { /* ignore */ }
    };
  }, [provider]);

  // ─── Data fetching ────────────────────────────────────────────────
  const refresh = useCallback(async () => {
    if (!provider || !view.enabled) return;
    // Skip while the GeoJSON source isn't yet attached. Calling
    // setSourceData against a non-existent source is a no-op (the
    // provider guards against it), but firing the post-fetch diagnostic
    // in that window logs a misleading "source MISSING / forcing
    // re-mount" warning and triggers the self-heal even though the
    // source effect's whenReady callback simply hasn't run yet.
    if (!sourceReady) {
      return;
    }
    if (deckEnabled) mapData?.setStatus({ loading: true, error: null, permissionDenied: false });
    try {
      const bounds = provider.getBounds();
      // Fetch spatial features from either the normalized map-API
      // (Phase 2) or the legacy render envelope. Both yield a
      // GeoJSON feature list the normalization below consumes.
      //
      // GeoLink's `/api/map/spatial-objects` GeoJSON endpoint rejects any
      // viewport whose bbox area exceeds ~100 deg² (`bbox_too_large`) — it's
      // meant for zoomed-in detail, with MVT tiles for wide overviews. So we
      // only use the map API when the current viewport is within that limit
      // and fall back to the legacy render envelope (which has no such cap)
      // for wide/zoomed-out views. This keeps objects visible at every zoom
      // instead of 400-ing on load. (TODO: swap the wide-view fallback for
      // MVT tiles once the deck MVTLayer path lands.)
      let sourceFeatures: SpatialObjectFeature[];
      const bboxAreaDeg2 = mapBboxAreaDeg2(bounds);
      const useMapApi = mapApiEnabled && bboxAreaDeg2 <= MAP_API_MAX_BBOX_DEG2;
      if (useMapApi) {
        const bbox: [number, number, number, number] = [
          bounds.sw.lng, bounds.sw.lat, bounds.ne.lng, bounds.ne.lat,
        ];
        let zoom: number | undefined;
        try { zoom = provider.getZoom(); } catch { zoom = undefined; }
        const fcRes = await mapApiClient.getSpatialObjects({ bbox, zoom, limit: 1000 });
        sourceFeatures = (fcRes.features ?? []) as unknown as SpatialObjectFeature[];
      } else {
        const envelope = await geoLinkSpatialObjectsClient.getVisibleSpatialObjectsRender(
          bounds,
          renderProvider,
        );
        sourceFeatures = envelope.featureCollection.features;
      }
      // Normalise + flatten properties:
      //   - `linkHas*` flags from executionRuleId / contractAddress so the
      //     layer filter expressions stay short.
      //   - Hoist `metadata.color_key` (and `metadata.colorKey`) into the
      //     top-level `colorKey` slot when GeoLink's render adapter didn't
      //     do it server-side. Without this, every object stays the default
      //     fallback colour because `buildExtrusionColorExpr` can only read
      //     top-level keys via Mapbox's `['get', …]` operator.
      //   - Same for the Mapbox `fill_color` provider alias, used by some
      //     GeoLink builds.
      // Flag palettes are read synchronously during normalization, so they
      // have to be in hand before it runs — warming afterwards would leave
      // the building in its ordinary colour until some later refresh.
      await warmFlagBands(flagCountriesIn(sourceFeatures));

      // Normalise + flatten each source feature (color hoist, canonical
      // objectId, link flags, and per-tier setback expansion for stepped
      // towers like Burj Khalifa). See `normalizeSpatialFeatures`.
      const fc: SpatialObjectFeatureCollection = {
        type: 'FeatureCollection',
        features: sourceFeatures.flatMap(normalizeSpatialFeatures),
      };
      // Merge this bbox's features into the long-lived accumulator so the
      // map + inspector keep showing everything we've fetched, not just
      // what's in the current (possibly tightly zoomed) viewport. Never
      // let a lossy post-create /render replace a just-imported tiered
      // building (local authority) or replace expanded #tN with a fat block.
      const acc = accumulatedRef.current;
      const incomingById = new Map<string, SpatialObjectFeature[]>();
      const orphan: SpatialObjectFeature[] = [];
      for (const feat of fc.features as SpatialObjectFeature[]) {
        const id = objectIdOf(feat);
        if (!id) { orphan.push(feat); continue; }
        const list = incomingById.get(id) ?? [];
        list.push(feat);
        incomingById.set(id, list);
      }
      for (const id of Array.from(incomingById.keys())) {
        const incoming = incomingById.get(id)!;
        if (isLocalAuthority(id)) continue;
        const existing = Array.from(acc.values()).filter((f) => objectIdOf(f) === id);
        if (
          featuresAreTierExpanded(existing) &&
          !featuresAreTierExpanded(incoming) &&
          tierCacheByObjectId.has(id)
        ) {
          continue;
        }
        upsertFeaturesIntoAcc(acc, incoming);
      }
      if (orphan.length) upsertFeaturesIntoAcc(acc, orphan);
      // Bound memory. Map preserves insertion order, so deleting from the
      // front drops the least-recently-(re)fetched objects first.
      const MAX_ACCUMULATED = 4000;
      if (acc.size > MAX_ACCUMULATED) {
        const overflow = acc.size - MAX_ACCUMULATED;
        const oldestKeys = Array.from(acc.keys()).slice(0, overflow);
        for (const k of oldestKeys) acc.delete(k);
      }
      const merged = Array.from(acc.values());
      const mergedFc: SpatialObjectFeatureCollection = { type: 'FeatureCollection', features: merged };
      provider.setSourceData(SPATIAL_SOURCE_ID, mergedFc as unknown as Parameters<typeof provider.setSourceData>[1]);
      setLoadedFeatures(merged);
      // Feed the deck.gl path (no-op when the deck renderer is off / no provider).
      if (deckEnabled) {
        mapData?.setSpatialObjects(merged);
        mapData?.setStatus({ loading: false, error: null, permissionDenied: false, empty: merged.length === 0 });
      }
      onDataLoaded?.(mergedFc);

      // ── Eager detail (both pipelines) ────────────────────────────────
      // After a hard refresh the in-memory tier cache is empty and /render
      // often returns GeometryCollection without expandable tiers — Atlantis
      // then paints mid-tower bands from the wrong base. Re-fetch admin /
      // detail for multi-part buildings (and any id we already persisted
      // tiers for) so setbacks come back without a click.
      {
        let eagerZoom: number | undefined;
        try { eagerZoom = provider.getZoom(); } catch { eagerZoom = undefined; }
        if (eagerZoom != null && eagerZoom >= EAGER_DETAIL_MIN_ZOOM) {
          const seen = new Set<string>();
          const candidates: string[] = [];
          for (const f of fc.features as SpatialObjectFeature[]) {
            const id = objectIdOf(f);
            if (!id || seen.has(id)) continue;
            const props = (f.properties ?? {}) as Record<string, unknown>;
            const alreadyTiered =
              Number(props.totalTiers) >= 2 ||
              String(f.id ?? '').includes('#t');
            if (alreadyTiered) continue;
            const parts = extractPolygonParts(f.geometry);
            const multiNeedsTiers = !!parts && parts.length > 1;
            const cachedTiers = tierCacheByObjectId.has(id);
            if (featureHasHeight(f) && !multiNeedsTiers && !cachedTiers) continue;
            seen.add(id);
            candidates.push(id);
            if (candidates.length >= EAGER_DETAIL_MAX_PER_REFRESH) break;
          }
          // Also re-hydrate any persisted tiered objects currently in the
          // accumulator that didn't expand (e.g. GeometryCollection + no
          // metadata.tiers on the wire).
          for (const id of Array.from(tierCacheByObjectId.keys())) {
            if (seen.has(id) || candidates.length >= EAGER_DETAIL_MAX_PER_REFRESH) break;
            const hasExpanded = Array.from(acc.keys()).some(
              (k) => k === `${id}#t0` || (k.startsWith(`${id}#t`) && k !== id),
            );
            if (hasExpanded) continue;
            if (!acc.has(id) && !Array.from(acc.keys()).some((k) => k.startsWith(`${id}#`))) {
              continue;
            }
            seen.add(id);
            candidates.push(id);
          }
          if (candidates.length) enrichRef.current?.(candidates);
        }
      }

      // Self-heal throttle: when Mapbox swaps the basemap style it
      // wipes every custom source/layer. We don't trigger an explicit
      // re-mount from here anymore (that produced infinite teardown
      // loops) — the source effect listens for `style.load` directly
      // — but we still record the most recent "missing" timestamp so
      // adjacent code paths can avoid hammering re-attach attempts.
      try {
        const native = provider.nativeMap as {
          getStyle?: () => { layers?: Array<{ id?: string }> };
          getSource?: (id: string) => unknown;
        };
        const haveSource   = !!native.getSource?.(SPATIAL_SOURCE_ID);
        const haveBuilding = !!native.getStyle?.()?.layers?.some(l => l.id === SPATIAL_LAYER_IDS.building);
        if (!haveSource || !haveBuilding) {
          const now = Date.now();
          if (now - lastSelfHealRef.current > 5_000) {
            lastSelfHealRef.current = now;
          }
        }
      } catch { /* ignore */ }
    } catch (err) {
      // Swallow so the refresh timer keeps running, but surface the
      // fetch state to the deck path so it can show error /
      // permission-denied affordances instead of a silent blank map.
      if (deckEnabled) {
        const status = (err as { status?: number } | null)?.status;
        const permissionDenied = status === 401 || status === 403;
        mapData?.setStatus({
          loading: false,
          permissionDenied,
          error: permissionDenied ? null : ((err as Error)?.message ?? 'Failed to load spatial objects'),
        });
      }
    }
  }, [provider, view.enabled, renderProvider, onDataLoaded, sourceReady, deckEnabled, mapData, mapApiEnabled]);

  // Keep the latest `refresh` reachable without making it an effect dep.
  useEffect(() => { refreshRef.current = refresh; }, [refresh]);

  // ─── Detail enrichment (Map API pipeline) ───────────────────────────
  // The /api/map/spatial-objects summary omits full geometry + height so a
  // bbox scan stays cheap. We fetch /spatial-objects/:id for the full
  // feature and swap it into the accumulator so the building re-renders
  // with its true footprint, height, and tiered setbacks. Triggered both
  // eagerly (buildings in view — see `refresh`) and on selection.
  const detailFetchedRef = useRef<Set<string>>(new Set());
  // Ref to the latest mapData so callers can read it without re-subscribing
  // (depending on the whole context would cancel in-flight work on any
  // unrelated state change — hover, pan, etc.).
  const mapDataRef = useRef(mapData);
  useEffect(() => { mapDataRef.current = mapData; }, [mapData]);

  // Batch-fetch full detail for a set of object ids and merge them into the
  // shared accumulator in a single source update. De-duped via
  // `detailFetchedRef`; a failed id is released so a later pass can retry.
  const enrichObjectsDetail = useCallback(async (ids: string[]) => {
    if (!provider || ids.length === 0) return;
    const toFetch = ids.filter(
      (id) =>
        id &&
        id !== 'undefined' &&
        !detailFetchedRef.current.has(id) &&
        // Keep just-created import geometry; enrich would pull lossy /render.
        !isLocalAuthority(id),
    );
    if (toFetch.length === 0) return;
    toFetch.forEach((id) => detailFetchedRef.current.add(id));

    const results = await Promise.all(
      toFetch.map(async (id) => {
        try {
          // Already painting correct setbacks from import/create — don't replace.
          const existing = Array.from(accumulatedRef.current.values()).filter(
            (f) => objectIdOf(f) === id,
          );
          if (featuresAreTierExpanded(existing) && tierCacheByObjectId.has(id)) {
            return { id, features: existing };
          }
          // 1) Map-API detail: normalized side-panel props (policyState,
          //    displayName, …). Tier-expand so stepped towers render their
          //    setbacks (not a single fat block).
          let detail: Awaited<ReturnType<typeof mapApiClient.getSpatialObject>> = null;
          try {
            detail = await mapApiClient.getSpatialObject(id);
          } catch { /* map API may be off in legacy pipeline */ }
          // Normalisation reads palettes synchronously, so a flag this
          // object is known (or now says) it wears must be in hand first.
          await warmFlagBands(
            flagCountriesIn([
              ...(detail ? [detail as unknown as SpatialObjectFeature] : []),
              { type: 'Feature', id, properties: {}, geometry: null } as unknown as SpatialObjectFeature,
            ]),
          );
          let features: SpatialObjectFeature[] = [];
          if (detail && detail.geometry) {
            const enriched = {
              ...detail,
              properties: { ...(detail.properties ?? {}), objectId: id, id },
            } as unknown as SpatialObjectFeature;
            features = normalizeSpatialFeatures(enriched);
          }

          // 2) Legacy `/:id/render` — often has geometry but strips
          //    metadata.tiers or rewrites MultiPolygon → GeometryCollection.
          const parts = extractPolygonParts(
            features[0]?.geometry ?? detail?.geometry ?? null,
          );
          const polyCount = parts?.length ?? 0;
          const needsTierFallback =
            !featuresAreTierExpanded(features) &&
            (polyCount > 1 || tierCacheByObjectId.has(id));
          if (!features.some(featureHasHeight) || needsTierFallback) {
            try {
              const env = await geoLinkSpatialObjectsClient.getSpatialObjectRender(
                id,
                renderProvider,
              );
              const renderFeat = env?.featureCollection?.features?.[0];
              if (renderFeat && renderFeat.geometry) {
                const merged = {
                  ...renderFeat,
                  properties: {
                    ...(detail?.properties ?? {}),
                    ...(renderFeat.properties ?? {}),
                    objectId: id,
                    id,
                  },
                } as unknown as SpatialObjectFeature;
                features = normalizeSpatialFeatures(merged);
              }
            } catch { /* non-fatal */ }
          }

          // 3) Admin list/detail — authoritative metadata.tiers + geometry
          //    after hard refresh (session caches are empty; /render is lossy).
          //    Prefer list+near: some GeoLink deploys 404 on GET /:id.
          if (!featuresAreTierExpanded(features)) {
            try {
              type AdminRow = Awaited<ReturnType<typeof geoLinkSpatialObjectsAdminClient.getObject>>;
              let row: AdminRow = null;
              try {
                row = await geoLinkSpatialObjectsAdminClient.getObject(id);
              } catch { /* 404 on some deploys */ }
              const metaTiers = parseTiersArray(
                (row?.metadata as { tiers?: unknown } | null | undefined)?.tiers,
              );
              if (!row || metaTiers.length < 2) {
                const partsForNear = extractPolygonParts(
                  features[0]?.geometry ?? detail?.geometry ?? null,
                );
                let lng = NaN;
                let lat = NaN;
                if (partsForNear && partsForNear[0]?.[0]?.[0]) {
                  const ring = partsForNear[0][0];
                  let sx = 0;
                  let sy = 0;
                  for (const c of ring) {
                    sx += Number(c[0]);
                    sy += Number(c[1]);
                  }
                  lng = sx / ring.length;
                  lat = sy / ring.length;
                }
                if (Number.isFinite(lng) && Number.isFinite(lat)) {
                  const list = await geoLinkSpatialObjectsAdminClient.listObjects({
                    near: { lng, lat, radius_meters: 800 },
                    limit: 100,
                  });
                  row = list.find((r) => r.id === id) ?? row;
                }
              }
              const meta = (row?.metadata ?? {}) as Record<string, unknown>;
              const tiers = parseTiersArray(meta.tiers);
              const geom = row?.geometry
                ? asMultiPolygonGeometry(row.geometry)
                : null;
              if (row && tiers.length > 0 && geom) {
                cacheUpsertDetail({
                  id,
                  geometry: geom,
                  tiers,
                  heightMeters: row.heightMeters ?? undefined,
                  baseHeightMeters: row.baseHeightMeters ?? undefined,
                  name: row.name ?? undefined,
                  objectType: row.objectType,
                  colorKey: typeof meta.color_key === 'string' ? meta.color_key : undefined,
                });
                const peak = tiers.reduce((m, t) => (t.height > m ? t.height : m), tiers[0].height);
                features = normalizeSpatialFeatures({
                  type: 'Feature',
                  id,
                  geometry: geom,
                  properties: {
                    objectId: id,
                    id,
                    name: row.name,
                    objectType: row.objectType,
                    heightMeters: peak,
                    height: peak,
                    baseHeightMeters: row.baseHeightMeters ?? 0,
                    metadata: meta,
                    colorKey: typeof meta.color_key === 'string' ? meta.color_key : undefined,
                  },
                } as unknown as SpatialObjectFeature);
              }
            } catch { /* no admin key — localStorage cache may still help */ }
          }

          // 4) Last resort: rebuild from persisted / in-memory tier cache.
          //    Tiers only pair with the geometry they were cached with — a
          //    wire fallback geometry may have fewer/reordered parts.
          if (!featuresAreTierExpanded(features) && tierCacheByObjectId.has(id)) {
            const tiers = tierCacheByObjectId.get(id)!;
            let geom: GeoJSON.Geometry | null = geometryCacheByObjectId.get(id) ?? null;
            if (!geom || !tierPairIsConsistent(tiers, geom)) {
              const wireGeom =
                asMultiPolygonGeometry(features[0]?.geometry) ??
                asMultiPolygonGeometry(detail?.geometry);
              geom = wireGeom && tierPairIsConsistent(tiers, wireGeom) ? wireGeom : null;
            }
            if (geom && tiers.length > 0) {
              const peak = tiers.reduce((m, t) => (t.height > m ? t.height : m), tiers[0].height);
              features = normalizeSpatialFeatures({
                type: 'Feature',
                id,
                geometry: geom,
                properties: {
                  ...(features[0]?.properties ?? {}),
                  objectId: id,
                  id,
                  heightMeters: peak,
                  height: peak,
                  metadata: {
                    ...(((features[0]?.properties as { metadata?: Record<string, unknown> } | undefined)?.metadata) ?? {}),
                    tiers,
                  },
                },
              } as unknown as SpatialObjectFeature);
            }
          }

          if (features.length === 0) {
            detailFetchedRef.current.delete(id);
            return null;
          }
          // Allow a later retry when we still didn't get per-tier setbacks
          // (admin key missing, transient 5xx, …) — otherwise Atlantis
          // stays wrong for the whole session after a hard refresh.
          if (!featuresAreTierExpanded(features)) {
            const stillMultipart =
              (extractPolygonParts(features[0]?.geometry)?.length ?? 0) > 1 ||
              tierCacheByObjectId.has(id);
            if (stillMultipart) detailFetchedRef.current.delete(id);
          }
          return { id, features };
        } catch {
          detailFetchedRef.current.delete(id);
          return null;
        }
      }),
    );

    const acc = accumulatedRef.current;
    let changed = false;
    const enrichedById = new Map<string, SpatialObjectFeature[]>();
    for (const r of results) {
      if (!r) continue;
      // Drop the summary entry + any prior per-tier features for this id,
      // then insert the freshly expanded detail feature(s).
      upsertFeaturesIntoAcc(acc, r.features);
      enrichedById.set(r.id, r.features);
      changed = true;
    }
    if (!changed) return;

    const merged = Array.from(acc.values());
    const mergedFc: SpatialObjectFeatureCollection = { type: 'FeatureCollection', features: merged };
    provider.setSourceData(SPATIAL_SOURCE_ID, mergedFc as unknown as Parameters<typeof provider.setSourceData>[1]);
    setLoadedFeatures(merged);
    if (deckEnabled) mapDataRef.current?.setSpatialObjects(merged);
    // Keep the open tooltip in sync with enriched geometry / country / height
    // (Map API summaries are sparse until /spatial-objects/:id returns).
    setSelectedFeature((prev) => {
      if (!prev) return prev;
      const pid = objectIdOf(prev);
      if (!pid || !enrichedById.has(pid)) return prev;
      const feats = enrichedById.get(pid)!;
      return feats.find((f) => !String(f.id ?? '').includes('#t')) ?? feats[0] ?? prev;
    });
  }, [provider, deckEnabled, renderProvider]);

  // Expose to `refresh` (eager path) without a direct dependency.
  useEffect(() => { enrichRef.current = enrichObjectsDetail; }, [enrichObjectsDetail]);

  // Enrich the locally selected object (native fill-extrusion clicks) —
  // MapData selection alone used to gate this, which broke Map API 3D
  // tooltips when deck wasn't owning spatial pick.
  useEffect(() => {
    if (!mapApiEnabled || !provider || !selectedFeature) return;
    const id = objectIdOf(selectedFeature);
    if (!id || id === 'undefined') return;
    void enrichObjectsDetail([id]);
  }, [mapApiEnabled, provider, selectedFeature, enrichObjectsDetail]);

  // Re-fetch + repaint when the rendering pipeline switches. The two
  // pipelines carry different property/geometry shapes (legacy /render
  // ships full geometry + height + tiers; the Map API summary does not),
  // so we clear the accumulator first — otherwise a non-tier "fat block"
  // from one pipeline could linger next to the expanded tier features of
  // the other. Skips the initial mount (the periodic effect below already
  // kicks off the first fetch).
  const didInitPipelineRef = useRef(false);
  useEffect(() => {
    if (!provider) return;
    if (!didInitPipelineRef.current) { didInitPipelineRef.current = true; return; }
    accumulatedRef.current.clear();
    detailFetchedRef.current.clear();
    setSelectedFeature(null);
    setAnchorPx(undefined);
    mapDataRef.current?.selectEntity(null);
    if (view.enabled) void refreshRef.current?.();
  }, [renderPipeline, provider, view.enabled]);

  // After a basemap style swap the GeoJSON source is re-created empty.
  // Re-push whatever we already have so extrusions redraw immediately
  // (Atlantis etc.) instead of waiting for the next camera/interval refresh.
  useEffect(() => {
    if (!provider || !sourceReady) return;
    const merged = Array.from(accumulatedRef.current.values());
    if (merged.length === 0) {
      if (view.enabled) void refreshRef.current?.();
      return;
    }
    const mergedFc: SpatialObjectFeatureCollection = {
      type: 'FeatureCollection',
      features: merged,
    };
    try {
      provider.setSourceData(
        SPATIAL_SOURCE_ID,
        mergedFc as unknown as Parameters<typeof provider.setSourceData>[1],
      );
    } catch { /* style mid-swap */ }
    setLoadedFeatures(merged);
    if (deckEnabled) {
      mapDataRef.current?.setSpatialObjects(merged);
    }
  }, [provider, sourceReady, styleEpoch, view.enabled, deckEnabled]);

  // Refresh on mount, on enable, and on a periodic timer. Intentionally does
  // NOT depend on `refresh` — see `refreshRef` above. Re-arms only when the
  // provider, enabled flag, or interval actually change.
  useEffect(() => {
    if (!provider || !view.enabled) return;
    void refreshRef.current?.();
    refreshTimerRef.current = setInterval(() => void refreshRef.current?.(), refreshIntervalMs);
    return () => {
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    };
  }, [provider, view.enabled, refreshIntervalMs]);

  // Refresh on camera moves, debounced so we don't spam the API.
  useEffect(() => {
    if (!provider || !view.enabled) return;
    const native = provider.nativeMap as { on?: (e: string, h: () => void) => void; off?: (e: string, h: () => void) => void };
    if (typeof native?.on !== 'function' || typeof native?.off !== 'function') return;
    const handler = () => {
      if (cameraDebounceRef.current) clearTimeout(cameraDebounceRef.current);
      cameraDebounceRef.current = setTimeout(() => { void refreshRef.current?.(); }, 600);
    };
    native.on('moveend', handler);
    return () => {
      try { native.off?.('moveend', handler); } catch { /* ignore */ }
      if (cameraDebounceRef.current) {
        clearTimeout(cameraDebounceRef.current);
        cameraDebounceRef.current = null;
      }
    };
  }, [provider, view.enabled]);

  // ── Objects that speak first ───────────────────────────────────────
  //
  // "May start the conversation" has been in the presence editor since
  // the beginning with nothing reading it. This is what reads it.
  //
  // Gated on the visitor having interacted with the page at least once,
  // because a browser will not play audio before that — a greeting
  // fired on page load would open a card that cannot be heard, which is
  // worse than not greeting at all.
  const greetedRef = useRef<Record<string, number>>({});
  const [hasGesture, setHasGesture] = useState(false);
  useEffect(() => {
    if (hasGesture) return undefined;
    const mark = () => setHasGesture(true);
    window.addEventListener('pointerdown', mark, { once: true });
    window.addEventListener('keydown', mark, { once: true });
    return () => {
      window.removeEventListener('pointerdown', mark);
      window.removeEventListener('keydown', mark);
    };
  }, [hasGesture]);

  useEffect(() => {
    // Never interrupt a conversation already in progress, and never
    // start one over an open panel: an object talking across a form the
    // visitor is filling in is an intrusion, not a welcome. A player
    // counts double — two voices at once is worse than either alone.
    if (!hasGesture || !location || chatTarget || listenTarget || selectedFeature) return;

    const target = pickProximityGreeting(
      loadedFeatures,
      { lat: location.latitude, lng: location.longitude },
      { greetedAt: greetedRef.current },
    );
    if (!target) return;

    // Recorded before the card opens, so a re-render cannot greet twice.
    greetedRef.current[target.objectId] = Date.now();

    let point = { x: window.innerWidth / 2, y: window.innerHeight / 3 };
    try {
      const native = provider?.nativeMap as {
        project?: (c: [number, number]) => { x: number; y: number };
      } | undefined;
      const projected = native?.project?.([target.center.lng, target.center.lat]);
      if (projected && Number.isFinite(projected.x) && Number.isFinite(projected.y)) {
        point = { x: projected.x, y: projected.y };
      }
    } catch { /* an unprojectable point just anchors centrally */ }

    setChatTarget({
      objectId: target.objectId,
      name: target.name,
      accent: target.accentColor,
      point,
      center: target.center,
    });
    // `openChat` is deliberately not used here: this greeting already
    // refuses to run while any other card is open, so closing them
    // would only ever close something that cannot be there.
  }, [hasGesture, location, loadedFeatures, chatTarget, listenTarget, selectedFeature, provider]);

  // Publish the accumulator to the GeoJSON source + deck path.
  const publishAccumulated = useCallback(() => {
    if (!provider) return;
    const merged = Array.from(accumulatedRef.current.values());
    const mergedFc: SpatialObjectFeatureCollection = { type: 'FeatureCollection', features: merged };
    try {
      provider.setSourceData(SPATIAL_SOURCE_ID, mergedFc as unknown as Parameters<typeof provider.setSourceData>[1]);
    } catch { /* source may be mid-rebuild */ }
    setLoadedFeatures(merged);
    if (deckEnabled) mapDataRef.current?.setSpatialObjects(merged);
  }, [provider, deckEnabled]);

  // How many people are at each event, fed into the seam that was
  // built for it. `setEventCounts` holds the numbers; this only asks.
  const eventCountVersion = useEventCounts({
    enabled: view.enabled,
    features: loadedFeatures,
    apply: setEventCounts,
  });

  // Restamp when a number actually changed, rather than re-fetching
  // every feature. The count is derived presentation: it goes onto the
  // GeoJSON properties the paint expression reads and nowhere near the
  // stored geometry or the event's metadata, which is why this can
  // rewrite it in place without writing anything back.
  useEffect(() => {
    if (!provider || !view.enabled) return;
    const acc = accumulatedRef.current;
    let touched = false;
    for (const key of Array.from(acc.keys())) {
      const feature = acc.get(key);
      const props = feature?.properties as Record<string, unknown> | null;
      if (!feature || !props) continue;
      const objectId = typeof props.objectId === 'string' ? props.objectId : null;
      if (!objectId) continue;

      const meta = (props.metadata as Record<string, unknown> | undefined) || {};
      // Through the one deriver, so an event that has since ended
      // loses its count for the same reason it loses its state.
      const derived = deriveEventPresentation(
        meta, Date.now(), eventCountByObjectId.get(objectId),
      );
      // Transient in the same way and for the same reason, so it
      // rides the same restamp rather than growing its own.
      const marked = objectId === highlightedObjectId ? true : undefined;
      if (props.eventCount === derived.eventCount
        && props.eventHighlighted === marked) continue;

      const next: Record<string, unknown> = { ...props, ...derived };
      // Omitted rather than zeroed or falsed when absent: missing
      // means "not known", and a value is a claim.
      if (derived.eventCount === undefined) delete next.eventCount;
      if (marked === undefined) delete next.eventHighlighted;
      else next.eventHighlighted = true;
      acc.set(key, { ...feature, properties: next } as SpatialObjectFeature);
      touched = true;
    }
    if (touched) publishAccumulated();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventCountVersion, highlightedObjectId, provider, view.enabled, publishAccumulated]);

  // Listen for an explicit refresh nudge from the admin overlay.
  // Soft refresh (default) merges the latest bbox into the accumulator —
  // that alone cannot drop deleted objects. Pass `{ hard: true }` (Reload
  // map layer) to wipe local state first, then re-fetch.
  useEffect(() => {
    if (!provider || !view.enabled) return;
    const handler = (ev: Event) => {
      const hard = !!(ev as CustomEvent<{ hard?: boolean }>).detail?.hard;
      if (hard) {
        accumulatedRef.current.clear();
        detailFetchedRef.current.clear();
        tierCacheByObjectId.clear();
        geometryCacheByObjectId.clear();
        wireSignatureByObjectId.clear();
        publishAccumulated();
      }
      void refreshRef.current?.();
    };
    window.addEventListener('geolink:spatial-refresh', handler as EventListener);
    return () => window.removeEventListener('geolink:spatial-refresh', handler as EventListener);
  }, [provider, view.enabled, publishAccumulated]);

  // Immediately drop a deleted object from the live map. Soft refresh after
  // delete cannot remove it because the accumulator only merges inbound
  // features and never prunes ids missing from the API response.
  useEffect(() => {
    if (!provider || !view.enabled) return;
    const handler = (ev: Event) => {
      const id = String((ev as CustomEvent<{ id?: string }>).detail?.id || '').trim();
      if (!id) return;
      clearCachedObject(id);
      detailFetchedRef.current.delete(id);
      removeObjectFromAcc(accumulatedRef.current, id);
      publishAccumulated();
      // Clear selection / popover if the deleted object was open.
      setSelectedFeature((prev) => {
        if (!prev) return prev;
        return objectIdOf(prev) === id ? null : prev;
      });
      const sel = mapDataRef.current?.state.selectedEntity;
      if (sel?.entityType === 'spatialObject' && String(sel.id).split('#')[0] === id) {
        mapDataRef.current?.selectEntity(null);
      }
    };
    window.addEventListener('geolink:spatial-object-remove', handler as EventListener);
    return () => window.removeEventListener('geolink:spatial-object-remove', handler as EventListener);
  }, [provider, view.enabled, publishAccumulated]);

  // After Manage Spatial Objects saves a tiered building, inject the local
  // geometry + tiers immediately. Closing the panel clears the temporary
  // import preview; without this, /render often returns a single-height
  // MultiPolygon and the tower snaps back to the "fat block" look.
  useEffect(() => {
    if (!provider || !view.enabled) return;
    const handler = (ev: Event) => {
      const detail = (ev as CustomEvent<SpatialObjectUpsertDetail>).detail;
      if (!detail?.id || !detail.geometry) return;
      cacheUpsertDetail(detail);
      markLocalAuthority(detail.id);
      // Block enrich from replacing this with lossy /render for a while.
      detailFetchedRef.current.add(detail.id);
      const heightM = Number(detail.heightMeters);
      const baseM = Number(detail.baseHeightMeters);
      const feature = {
        type: 'Feature',
        id: detail.id,
        geometry: detail.geometry,
        properties: {
          objectId: detail.id,
          id: detail.id,
          name: detail.name,
          objectType: detail.objectType,
          colorKey: detail.colorKey,
          fill_color: detail.colorKey,
          heightMeters: Number.isFinite(heightM) ? heightM : undefined,
          height: Number.isFinite(heightM) ? heightM : undefined,
          baseHeightMeters: Number.isFinite(baseM) ? baseM : 0,
          base_height: Number.isFinite(baseM) ? baseM : 0,
          metadata: {
            ...(detail.colorKey ? { color_key: detail.colorKey } : {}),
            ...(detail.tiers && detail.tiers.length > 0 ? { tiers: detail.tiers } : {}),
          },
        },
      } as unknown as SpatialObjectFeature;
      const expanded = normalizeSpatialFeatures(feature);
      upsertFeaturesIntoAcc(accumulatedRef.current, expanded);
      publishAccumulated();
      // Do NOT soft-refresh here — /render right after create often returns
      // GeometryCollection / stripped tiers and immediately ruins Atlantis.
    };
    window.addEventListener('geolink:spatial-object-upsert', handler as EventListener);
    return () => window.removeEventListener('geolink:spatial-object-upsert', handler as EventListener);
  }, [provider, view.enabled, publishAccumulated]);

  // ─── Click + hover handlers ───────────────────────────────────────
  const interactiveLayerIds = useMemo(() => [
    SPATIAL_LAYER_IDS.zone,        // zones top — most likely intended target
    SPATIAL_LAYER_IDS.slice,
    SPATIAL_LAYER_IDS.floor,
    SPATIAL_LAYER_IDS.building,
    SPATIAL_LAYER_IDS.point,       // placeholder Point markers — clickable
  ], []);

  useEffect(() => {
    if (!provider) return;
    const handlers: Array<() => void> = [];

    interactiveLayerIds.forEach(layerId => {
      const onClick = (e: import('../../services/spatial/mapProvider').ProviderMapEvent) => {
        const top = e.features[0];
        if (!top) return;
        // Click features come back from the map as a generic
        // `Record<string, unknown>` — go through `unknown` so the
        // narrow SpatialObjectProperties cast is explicit.
        const rawProps = { ...((top.properties ?? {}) as Record<string, unknown>) };
        let feature = {
          type: 'Feature',
          id: top.id,
          geometry: top.geometry,
          properties: rawProps,
        } as unknown as SpatialObjectFeature;
        const id = objectIdOf(feature);
        if (id && (feature.properties as { objectId?: string }).objectId == null) {
          (feature.properties as { objectId?: string }).objectId = id;
        }
        // Mapbox click payloads often drop nested `metadata.tiers` and leave
        // only the clicked setback's height (e.g. 35m). Rebuild peak from
        // every in-memory tier feature + tier cache for the tooltip.
        if (id) {
          const parseMeta = (raw: unknown): Record<string, unknown> => {
            if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
              return { ...(raw as Record<string, unknown>) };
            }
            if (typeof raw === 'string' && raw.trim().startsWith('{')) {
              try {
                const p = JSON.parse(raw) as unknown;
                if (p && typeof p === 'object' && !Array.isArray(p)) {
                  return { ...(p as Record<string, unknown>) };
                }
              } catch { /* ignore */ }
            }
            return {};
          };
          const acc = accumulatedRef.current;
          const siblings = Array.from(acc.values()).filter((f) => objectIdOf(f) === id);
          const fromAcc = acc.get(id) || siblings[0];
          const cachedTiers = tierCacheByObjectId.get(id);
          const accMeta = {
            ...(fromAcc ? parseMeta((fromAcc.properties as { metadata?: unknown }).metadata) : {}),
            ...parseMeta((feature.properties as { metadata?: unknown }).metadata),
          };
          if (cachedTiers && cachedTiers.length > 0 && !Array.isArray(accMeta.tiers)) {
            accMeta.tiers = cachedTiers;
          }
          let peak = NaN;
          let tierCount = 0;
          if (Array.isArray(accMeta.tiers)) {
            for (const t of accMeta.tiers as Array<{ height?: number }>) {
              const h = Number(t?.height);
              if (Number.isFinite(h)) {
                tierCount += 1;
                peak = Number.isFinite(peak) ? Math.max(peak, h) : h;
              }
            }
          }
          // Also scan every expanded #tN feature in the accumulator.
          for (const f of siblings) {
            const h = Number((f.properties as { heightMeters?: number }).heightMeters);
            if (Number.isFinite(h) && h > 0) {
              peak = Number.isFinite(peak) ? Math.max(peak, h) : h;
            }
          }
          if (cachedTiers && cachedTiers.length >= 2) {
            for (const t of cachedTiers) {
              peak = Number.isFinite(peak) ? Math.max(peak, t.height) : t.height;
            }
            if (tierCount < cachedTiers.length) tierCount = cachedTiers.length;
          }
          const baseProps = fromAcc
            ? { ...fromAcc.properties, ...feature.properties }
            : { ...feature.properties };
          feature = {
            ...(fromAcc ?? feature),
            properties: {
              ...baseProps,
              objectId: id,
              metadata: accMeta,
              ...(Number.isFinite(peak) && peak > 0
                ? { heightMeters: peak, height: peak, peakHeightMeters: peak }
                : {}),
              ...(tierCount >= 2 ? { totalTiers: tierCount } : {}),
            },
          } as SpatialObjectFeature;
        }
        // On a phone the panel takes the screen, so the cards it would
        // otherwise be stacked under close first. Called before the
        // selection so the panel is never briefly the thing that gets
        // closed by its own arrival.
        closeCompetingCards('inspector');
        // Native fill-extrusion owns paint in BOTH pipelines — always drive
        // the interaction panel from local selection. Also mirror into
        // MapData when present so aggregate tooling stays consistent.
        setSelectedFeature(feature);
        setAnchorPx(e.point);
        // A click pins: the panel below now survives the pointer
        // leaving, and the transient card gets out of its way.
        hoverActions.pin(feature, e.point);
        mapDataRef.current?.selectSpatialObject?.(feature, e.point);
        if (id) enrichRef.current?.([id]);
      };
      const onEnter = (e: import('../../services/spatial/mapProvider').ProviderMapEvent) => {
        const native = provider.nativeMap as { getCanvas?: () => HTMLCanvasElement };
        try { if (native.getCanvas) native.getCanvas().style.cursor = 'pointer'; } catch { /* ignore */ }
        // Transient hover: the card is built from properties already in
        // the browser, so this costs nothing. Anything that needs a
        // request waits for a click.
        const top = e.features[0];
        if (!top) return;
        hoverActions.enter(
          {
            type: 'Feature',
            id: top.id,
            geometry: top.geometry,
            properties: { ...((top.properties ?? {}) as Record<string, unknown>) },
          } as unknown as SpatialObjectFeature,
          e.point,
        );
      };
      const onHoverMove = (e: import('../../services/spatial/mapProvider').ProviderMapEvent) => {
        hoverActions.move(e.point);
      };
      const onLeave = () => {
        const native = provider.nativeMap as { getCanvas?: () => HTMLCanvasElement };
        try { if (native.getCanvas) native.getCanvas().style.cursor = ''; } catch { /* ignore */ }
        hoverActions.leave();
      };

      provider.on('click', layerId, onClick);
      provider.on('mouseenter', layerId, onEnter);
      provider.on('mousemove', layerId, onHoverMove);
      provider.on('mouseleave', layerId, onLeave);

      handlers.push(() => provider.off('click', layerId, onClick));
      handlers.push(() => provider.off('mouseenter', layerId, onEnter));
      handlers.push(() => provider.off('mousemove', layerId, onHoverMove));
      handlers.push(() => provider.off('mouseleave', layerId, onLeave));
    });

    return () => { handlers.forEach(h => { try { h(); } catch { /* ignore */ } }); };
  }, [provider, interactiveLayerIds, hoverActions, closeCompetingCards]);

  // Master enable toggle.
  useEffect(() => {
    if (!provider) return;
    Object.values(SPATIAL_LAYER_IDS).forEach(id => {
      if (provider.hasLayer(id)) {
        provider.setLayerVisibility(id, view.enabled);
      }
    });
  }, [provider, view.enabled]);

  if (!provider) {
    return showControls
      ? <SpatialObjectsControls provider={null} style={controlsStyle} />
      : null;
  }

  return (
    <>
      {/* The `key={styleEpoch}` forces React to unmount + remount each
          spatial layer whenever the basemap style reloads (Mapbox
          wipes our custom sources/layers on every setStyle). The
          unmount cleanup runs `provider.removeLayer(...)` (a no-op
          when the layer is already gone) and the new mount re-adds
          the layer on top of the fresh style. Without this, the data
          refresh successfully writes to a now-non-existent source and
          nothing renders.

          The `sourceReady` gate ensures the GeoJSON source has
          finished initialising before any child mounts — otherwise
          their addLayer fires before the source exists and Mapbox
          drops it silently. */}
      {/* Native Mapbox fill-extrusion for spatial objects in BOTH pipelines.
          deck.gl GeoJsonLayer has no fill-extrusion-base, so tiered towers
          (Atlantis The Royal, Burj Khalifa, …) rendered as solid pillars from
          the ground and looked taller/deformed under "Map API 3D". Native
          extrusions honour per-tier min_height→height exactly like Accurate 3D.
          Map API 3D still *fetches* via /api/map (+ detail enrichment); only
          the paint path stays native. */}
      {sourceReady && (
        <>
          <SpatialObjectExtrusionLayer key={`bld-${styleEpoch}`} provider={provider} />
          <SpatialObjectFloorLayer     key={`flr-${styleEpoch}`} provider={provider} />
          <SpatialObjectZoneLayer      key={`zon-${styleEpoch}`} provider={provider} />
          <SpatialObjectSliceLayer     key={`slc-${styleEpoch}`} provider={provider} />
          <SpatialObjectPointLayer     key={`pnt-${styleEpoch}`} provider={provider} />
        </>
      )}

      {/* Deck.gl overlay for Phase 2 aggregate layers only (country policy,
          rules, presence, agents, drones). Spatial objects stay on native
          fill-extrusion above so stepped buildings stay accurate. */}
      {deckOverlayMounted && (
        <SpatialDeckOverlay
          map={provider.nativeMap}
          enabled={view.enabled}
          interleaved={deckInterleaved}
          renderSpatialObjects={false}
        />
      )}

      {/* Headless feeder for the aggregate layers (country policy, rules,
          presence, agents, drones) → MapDataContext, with WS deltas. */}
      {aggregateEnabled && (
        <MapAggregateLayersController provider={provider} enabled={view.enabled} />
      )}

      {showControls && (
        <SpatialObjectsControls provider={provider} style={controlsStyle} />
      )}

      {/* What is live within earshot of the listener. Read-only: nothing
          here starts a broadcast, it only offers a way into one that is
          already running. */}
      {view.enabled && (
        <SpatialNearbyPanel
          location={location}
          wallet={publicKey}
          // Whatever the map card is already playing. Two players in one
          // room is an echo, and `autoSurfacePlayer` would cause it
          // without anybody asking.
          excludeObjectId={listenTarget?.objectId ?? null}
        />
      )}

      {showTiles3D && (
        <Tiles3DLoaderPanel map={provider.nativeMap} />
      )}

      {showInspector && sourceReady && (
        <SpatialInspector
          provider={provider}
          features={loadedFeatures}
          epoch={styleEpoch}
          onSelectFeature={(f) => {
            // Declare the intent and let the coordinator decide the
            // camera. The clamping, the deferral, the jumpTo fallback
            // and the reason each exists now live in `mapActions`,
            // where a second caller inherits them instead of
            // rediscovering the render-loop crash they prevent.
            mapActions?.focusSpatialObject({ geometry: f.geometry });
            setSelectedFeature(f);
          }}
        />
      )}

      {/* Transient hover card. Reads only what the feature already
          carries — no request is made for hovering a building. */}
      {hoverTooltip && (
        <SpatialObjectTooltip
          model={hoverTooltip.model}
          anchor={hoverTooltip.point}
          onMouseEnter={hoverActions.cardEnter}
          onMouseLeave={hoverActions.cardLeave}
          // Offered whenever the object has an id to talk to. The card
          // that says an object talks has to be the card that lets you
          // talk to it; the server decides whether it will answer.
          onChat={hoverTooltip.model.objectId ? () => {
            const { model, point, feature } = hoverTooltip;
            // Pinning keeps the transient card out of the
            // conversation's way without opening the details panel.
            hoverActions.pin(feature, point);
            openChat({
              objectId: model.objectId as string,
              name: model.name,
              accent: model.accentColor,
              point,
              center: featureCenter(feature),
            });
          } : undefined}
          // Offered unless we know there is nothing to hear. The card
          // resolves the session itself, using what the map-wide poll
          // already knows when it knows it.
          onListen={hoverTooltip.model.objectId ? () => {
            const { model, point, feature } = hoverTooltip;
            hoverActions.pin(feature, point);
            closeCompetingCards('listen');
            const center = featureCenter(feature);
            setListenTarget({
              objectId: model.objectId as string,
              name: model.name,
              accent: model.accentColor,
              point,
              // Shown, not enforced. The gain is left at full: the
              // visitor deliberately opened a player, and attenuating a
              // building three cities away to silence would answer a
              // question they did not ask.
              distanceMeters: location && center
                ? haversineMeters(
                  { lat: location.latitude, lng: location.longitude },
                  center,
                )
                : null,
            });
          } : undefined}
          // Offered whenever there is an object to fund. Whether it can
          // actually receive is the chain's answer, and the card asks
          // for it as soon as it opens rather than guessing here.
          onDeposit={hoverTooltip.model.objectId ? () => {
            const { model, point, feature } = hoverTooltip;
            hoverActions.pin(feature, point);
            closeCompetingCards('deposit');
            setDepositTarget({
              objectId: model.objectId as string,
              name: model.name,
              accent: model.accentColor,
              point,
            });
          } : undefined}
          // Through `mapActions`, not by setting the state directly.
          // The card would open either way; routing it through the
          // vocabulary is what keeps one path for "show me that
          // event" whether a person, a list or the AI asked for it.
          onEvent={hoverTooltip.model.objectId ? () => {
            const { model, point, feature } = hoverTooltip;
            hoverActions.pin(feature, point);
            closeCompetingCards('event');
            setAnchorPx(point);
            mapActions?.openEvent({ objectId: model.objectId as string });
          } : undefined}
          onDetails={() => {
            hoverActions.pin(hoverTooltip.feature, hoverTooltip.point);
            setSelectedFeature(hoverTooltip.feature);
            setAnchorPx(hoverTooltip.point);
          }}
        />
      )}

      {/* The conversation itself. Anchored where it was opened, and
          outliving the pointer: a chat that vanished on mouseleave
          would be unusable. */}
      {chatTarget && (
        <SpatialObjectChatCard
          objectId={chatTarget.objectId}
          objectName={chatTarget.name}
          accentColor={chatTarget.accent}
          anchor={chatTarget.point}
          objectCenter={chatTarget.center ?? null}
          wallet={publicKey ?? null}
          location={location ?? null}
          // The host's 3D move, handed over rather than reimplemented — the
          // same `cinematicFlyTo` the inspector uses. Null when the host gave
          // us none or the object has no centre to fly to, and the card then
          // offers no control, because a button that does nothing is worse
          // than no button.
          //
          // Framing the building does not close the conversation: nothing
          // here touches `chatTarget`, so the card stays anchored and the
          // camera moves underneath it.
          onCinematic={cinematicFlyTo && chatTarget.center
            ? () => cinematicFlyTo([chatTarget.center!.lng, chatTarget.center!.lat])
            : null}
          onClose={() => {
            setChatTarget(null);
            hoverActions.unpin();
          }}
        />
      )}

      {/* The player, anchored where it was opened. Nothing here starts a
          broadcast: if the object is quiet the card says so. */}
      {listenTarget && (
        <SpatialObjectListenCard
          objectId={listenTarget.objectId}
          objectName={listenTarget.name}
          accentColor={listenTarget.accent}
          anchor={listenTarget.point}
          known={liveAudio.get(listenTarget.objectId)}
          distanceMeters={listenTarget.distanceMeters}
          wallet={publicKey ?? null}
          onClose={() => {
            setListenTarget(null);
            hoverActions.unpin();
          }}
        />
      )}

      {/* The event surface. Opened through `mapActions.openEvent`,
          which is why nothing that asks for it knows where it lands:
          the anchor is this layer's, and the surface ranking is
          `mapSurfaces`'. */}
      {eventTarget && (
        <SpatialEventCard
          eventObjectId={eventTarget}
          anchor={anchorPx ?? { x: 80, y: 120 }}
          mapActions={mapActions}
          geometry={
            accumulatedRef.current.get(eventTarget)?.geometry
            ?? geometryCacheByObjectId.get(eventTarget)
          }
          wallet={publicKey ?? null}
          /* Drawn, not decided. The server authorizes every one of
             these calls and refuses regardless of what was drawn. */
          canManage={!!publicKey}
          onEdit={(id) => {
            setEventEditTarget({ hostObjectId: eventTarget, eventObjectId: id });
            setEventTarget(null);
          }}
          onClose={() => {
            setEventTarget(null);
            setHighlightedObjectId(null);
          }}
        />
      )}

      {/* Creating or editing one. Opened from the venue, because the
          person putting on an event is standing in front of the
          building they want to use. */}
      {eventEditTarget && (
        <SpatialEventComposer
          hostObjectId={eventEditTarget.hostObjectId}
          eventObjectId={eventEditTarget.eventObjectId ?? null}
          anchor={anchorPx ?? { x: 80, y: 120 }}
          wallet={publicKey ?? null}
          onSaved={(id) => {
            setEventEditTarget(null);
            setEventTarget(id);
            void refresh();
          }}
          onClose={() => setEventEditTarget(null)}
        />
      )}

      {/* Funding an object. Outlives the pointer for the same reason
          the conversation does: a payment form that vanished on
          mouseleave would be worse than no payment form. */}
      {depositTarget && (
        <SpatialObjectDepositCard
          objectId={depositTarget.objectId}
          objectName={depositTarget.name}
          accentColor={depositTarget.accent}
          anchor={depositTarget.point}
          status={treasury.status}
          loading={treasury.phase === 'loading'}
          wallet={publicKey ?? null}
          signer={bridges?.signXdr
            ? { signTransaction: bridges.signXdr }
            : null}
          onDeposited={() => { void treasury.refresh(); void refresh(); }}
          onClose={() => {
            setDepositTarget(null);
            hoverActions.unpin();
          }}
        />
      )}

      <SpatialObjectInteractionPanel
        feature={selectedFeature}
        anchorPx={anchorPx}
        publicKey={publicKey ?? undefined}
        location={location ?? undefined}
        bridges={bridges}
        preconditionsFor={preconditionsFor}
        // Opening goes through `mapActions` so there is one path for
        // "show me that event"; creating does not, because there is
        // no event to show yet and the composer belongs to the venue.
        onOpenEvents={({ objectId, create }) => {
          closeCompetingCards('event');
          if (create) setEventEditTarget({ hostObjectId: objectId });
          else mapActions?.openEvent({ objectId });
        }}
        // The door a touch device needs. `anchorPx` is where the tap
        // landed, so the conversation opens over the same building the
        // panel was describing, and the object's own centre travels
        // with it for the card's shortcuts.
        onChat={selectedFeature ? ({ objectId }) => {
          const model = deriveSpatialTooltipModel(selectedFeature);
          openChat({
            objectId,
            name: model?.name || 'This object',
            accent: model?.accentColor || '#6366f1',
            point: anchorPx ?? { x: window.innerWidth / 2, y: window.innerHeight / 3 },
            center: featureCenter(selectedFeature),
          });
        } : undefined}
        onClose={() => {
          setSelectedFeature(null);
          setAnchorPx(undefined);
          hoverActions.unpin();
          mapData?.selectEntity(null);
        }}
        onActionCompleted={() => {
          // Refresh after a successful action so any state change
          // is reflected on the map.
          void refresh();
        }}
      />

      {/* Read-only detail card for a picked aggregate entity (country,
          rule, agent, presence cell, drone). */}
      {aggregateEnabled && <MapEntityPopover />}
    </>
  );
};

const SpatialObjectsMapLayer: React.FC<SpatialObjectsMapLayerProps> = (props) => {
  return (
    <SpatialObjectsProvider
      initialView={props.initialView}
      initialFilters={props.initialFilters}
      initialMapTilerOverlay={props.initialMapTilerOverlay}
    >
      <MapDataProvider>
        <SpatialObjectsMapLayerInner {...props} />
      </MapDataProvider>
    </SpatialObjectsProvider>
  );
};

export default SpatialObjectsMapLayer;
